import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  FileText,
  Search,
  Clock,
  Star,
  Zap,
  Eye,
  BarChart3,
  TrendingDown,
  LinkIcon,
  ChevronRight,
  AlertCircle,
  MessageSquare,
  Download,
  Layers,
  Link as LucideLink,
  FileArchive,
  Loader2,
  Globe2,
  ShieldCheck,
  BadgeCheck,
  Lock,
  TrendingUp,
  CalendarX,
  Upload,
  Check,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { useForceLightMode } from '../hooks/useForceLightMode';

export const SocialDemoPage: React.FC = () => {
  useForceLightMode();
  const isRecordMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('record');
  // viewMode: 'simplified' = the animated demo carousel; 'actual' = the recorded product tour mp4
  const [viewMode, setViewMode] = useState<'simplified' | 'actual'>('simplified');
  // CSS scale factor for the booth carousel — authored target is 1280×720 (16:9) so it fills a full-HD TV without letterboxing.
  const [stageScale, setStageScale] = useState(1);
  useEffect(() => {
    const update = () => {
      const targetW = 1280;
      const targetH = 720;
      setStageScale(Math.min(window.innerWidth / targetW, window.innerHeight / targetH));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [started, setStarted] = useState(false);
  const [activeScreen, setActiveScreen] = useState(0);
  const [filterPhase, setFilterPhase] = useState<'idle' | 'typing' | 'filtered' | 'citation' | 'citation-1' | 'citation-2'>('idle');
  const [eventClickRipple, setEventClickRipple] = useState<number | null>(null);
  const [chronoScrollY, setChronoScrollY] = useState(0); // pixels of upward scroll on the chronology content (negative = scrolled down)
  const [typedChars, setTypedChars] = useState(0);
  const filterQuery = 'show MS relapses with steroid treatment';
  const [expandedPeriod, setExpandedPeriod] = useState<number | null>(null); // 0-3, null = collapsed
  const [periodClickRipple, setPeriodClickRipple] = useState<number | null>(null);
  const [reportPhase, setReportPhase] = useState<'options' | 'selected' | 'drafting'>('options');
  const [reportGenChars, setReportGenChars] = useState(0);
  const [featuresRevealed, setFeaturesRevealed] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [filesRevealed, setFilesRevealed] = useState(0);
  const [fileStatuses, setFileStatuses] = useState<string[]>(['Processing', 'Processing', 'Processing', 'Processing']);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState<'dropping' | 'processing' | 'done'>('dropping');
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [spokenWordIndex, setSpokenWordIndex] = useState(-1);
  const [bundlePhase, setBundlePhase] = useState<'idle' | 'building' | 'ready' | 'viewing'>('idle');
  const [bundleProgress, setBundleProgress] = useState(0);
  const [bundleScroll, setBundleScroll] = useState(0); // 0-100 simulated scroll position
  const [overviewScroll, setOverviewScroll] = useState(0); // 0-100 scroll for overview page
  const overviewContentRef = useRef<HTMLDivElement>(null);
  const [overviewMaxScroll, setOverviewMaxScroll] = useState(0); // measured max translate in pixels
  const [bundleHighlight, setBundleHighlight] = useState(-1); // currently highlighted bundle tile (0-2)
  const [analysisHighlight, setAnalysisHighlight] = useState(-1); // currently highlighted analysis tile (0-2)
  const [ctaVariant, setCtaVariant] = useState<'demo' | 'signup' | 'booth' | 'banner1' | 'banner2'>(() => {
    if (typeof window === 'undefined') return 'booth';
    const params = new URLSearchParams(window.location.search);
    const cta = params.get('cta');
    if (cta === 'demo') return 'demo';
    if (cta === 'signup') return 'signup';
    if (cta === 'banner1') return 'banner1';
    if (cta === 'banner2') return 'banner2';
    // Default is booth — this page is now the booth display. Other variants are opt-in via ?cta= query.
    return 'booth';
  });
  const [introStep, setIntroStep] = useState(0); // 0=hidden, 1=cyan node, 2=top node, 3=right node, 4=lines, 5=text, 6=tagline, 7=divider, 8=headline
  const [boothSplashStep, setBoothSplashStep] = useState(0); // 0=hidden, 1=cyan, 2=top, 3=right, 4=lines, 5=text, 6=tagline, 7=cta-block
  const audioRefs = useRef<HTMLAudioElement[]>([]);
  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number>(0);

  // Pre-load voiceover audio files (generated by scripts/generate-voiceover.sh)
  useEffect(() => {
    const files = [
      '/audio/voiceover/screen-0.mp3',
      '/audio/voiceover/screen-1.mp3',
      '/audio/voiceover/screen-2.mp3',
      '/audio/voiceover/screen-3.mp3',
      '/audio/voiceover/screen-4.mp3',
      '/audio/voiceover/screen-5.mp3',
      '/audio/voiceover/screen-6.mp3',
      '/audio/voiceover/screen-7.mp3',
      ctaVariant === 'signup' ? '/audio/voiceover/screen-8-signup.mp3' : '/audio/voiceover/screen-8.mp3',
    ];
    let loaded = 0;
    audioRefs.current = files.map((src) => {
      const audio = new Audio(src);
      audio.preload = 'auto';
      audio.addEventListener('canplaythrough', () => {
        loaded++;
        if (loaded === files.length) setAudioLoaded(true);
      }, { once: true });
      // Don't block if audio files aren't present
      audio.addEventListener('error', () => {
        loaded++;
        if (loaded === files.length) setAudioLoaded(true);
      }, { once: true });
      return audio;
    });
    // Background music
    const bg = new Audio('/audio/background.mp3');
    bg.loop = true;
    bg.volume = 0.12;
    bg.preload = 'auto';
    bgMusicRef.current = bg;

    // Mark loaded after 2s even if files aren't found
    const fallback = setTimeout(() => setAudioLoaded(true), 2000);
    return () => clearTimeout(fallback);
  }, [ctaVariant]);

  // Countdown: click anywhere to start a 3s countdown, then begin playback
  const startCountdown = useCallback(() => {
    if (countdown !== null || started) return;
    setCountdown(3);
  }, [countdown, started]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      setStarted(true);
      setCountdown(null);
      return;
    }
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  // Background music — start when carousel starts, fade out when it ends
  useEffect(() => {
    const bg = bgMusicRef.current;
    if (!bg) return;
    if (started) {
      bg.currentTime = 0;
      bg.volume = 0;
      bg.play().catch(() => {});
      // Fade in over 1s
      const fadeIn = setInterval(() => {
        if (bg.volume < 0.12) {
          bg.volume = Math.min(0.12, bg.volume + 0.01);
        } else {
          clearInterval(fadeIn);
        }
      }, 80);
      return () => clearInterval(fadeIn);
    } else {
      bg.pause();
      bg.currentTime = 0;
    }
  }, [started]);

  // Fade out music on the final screen, but only AFTER the voiceover finishes
  useEffect(() => {
    const bg = bgMusicRef.current;
    if (!bg || !started) return;
    if (activeScreen === screenCount - 1) {
      const ctaAudio = audioRefs.current[screenCount - 1];
      // Wait until the CTA voiceover has finished before starting to fade.
      // Fall back to a fixed delay if duration isn't available.
      const ctaDurationMs = (ctaAudio?.duration && isFinite(ctaAudio.duration))
        ? ctaAudio.duration * 1000
        : 11000;
      const fadeStartDelay = ctaDurationMs + 800; // small pause after voice ends

      const startFade = setTimeout(() => {
        const fadeOut = setInterval(() => {
          if (bg.volume > 0.002) {
            bg.volume = Math.max(0, bg.volume - 0.002);
          } else {
            bg.pause();
            clearInterval(fadeOut);
          }
        }, 100);
        (startFade as unknown as { _interval?: ReturnType<typeof setInterval> })._interval = fadeOut;
      }, fadeStartDelay);

      return () => {
        clearTimeout(startFade);
        const inner = (startFade as unknown as { _interval?: ReturnType<typeof setInterval> })._interval;
        if (inner) clearInterval(inner);
      };
    }
  }, [activeScreen, started]);

  // Play the matching voiceover when the screen changes (only after started)
  useEffect(() => {
    // Stop all audio
    audioRefs.current.forEach((a) => {
      a.pause();
      a.currentTime = 0;
    });
    if (!isPaused && started && audioLoaded && audioRefs.current[activeScreen]) {
      const delay = activeScreen === 0 ? 1000 : 0;
      const timer = setTimeout(() => {
        audioRefs.current[activeScreen]?.play().catch(() => {});
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [activeScreen, isPaused, audioLoaded, started]);

  // Track spoken words by polling audio currentTime
  useEffect(() => {
    setSpokenWordIndex(-1);
    const audio = audioRefs.current[activeScreen];
    if (!audio) return;

    const words = screenCaptions[activeScreen].split(/\s+/);
    const totalWords = words.length;

    const tick = () => {
      if (audio.paused && audio.currentTime === 0) {
        // Audio hasn't started yet
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      if (audio.duration && isFinite(audio.duration) && !audio.paused) {
        const progress = audio.currentTime / audio.duration;
        const wordIdx = Math.floor(progress * totalWords);
        setSpokenWordIndex(Math.min(wordIdx, totalWords - 1));
      } else if (audio.ended) {
        setSpokenWordIndex(totalWords - 1);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [activeScreen]);

  const reportSections = [
    { heading: 'Introduction', body: 'This report has been prepared in relation to a personal injury claim arising from a road traffic collision on 11 November 2024. The claimant is Jane Doe, date of birth 1 May 1965, currently aged 59, who resides at 14 Maple Close, Woking, Surrey, GU22 8AB.' },
    { heading: '', body: 'The purpose of this report is to provide an objective summary of the medical records relevant to this claim, covering the claimant\u2019s pre-existing medical history, the acute injuries sustained in the collision, the treatment received during a 16-day inpatient admission at Royal Woking Hospital, and the post-discharge rehabilitation and follow-up care extending into 2026. The report is intended for a lay reader and aims to present the documented facts in plain English without the use of unnecessary clinical terminology.' },
    { heading: 'Records Reviewed', body: 'The following records have been reviewed in the preparation of this report: general practice records; hospital inpatient records from Royal Woking Hospital relating to the admission of 11 to 27 November 2024; ambulance records dated 11 November 2024; neurology outpatient clinic letters and MS nurse review records spanning 1992 to 2026; urology outpatient records; community physiotherapy records; occupational therapy records; and orthopaedic fracture clinic correspondence.' },
    { heading: 'Background', body: 'Jane Doe has a longstanding diagnosis of multiple sclerosis, first established in 1992, which had progressed to the secondary progressive form by 2009. She took ill-health retirement from her career as a ward nurse in 2010 as a result of MS-related disability. In the period immediately before the collision, she was able to walk approximately 50 metres using a wheeled walking frame and used a mobility scooter for longer distances. She lives alone in a two-storey property, with her adult daughter residing nearby and providing practical support.' },
    { heading: 'The Index Event', body: 'On 11 November 2024, Jane Doe was involved in a rear-end road traffic collision on the A247 near Send, Surrey. Her stationary vehicle was struck from behind at a reported speed of approximately 35 miles per hour. She sustained an acute left frontoparietal subdural haematoma, a right parietal linear skull fracture, acute non-displaced spinous process fractures at C7, T1, and T2, and a severe right hip abductor mechanism injury involving partial tearing of the gluteus medius and minimus tendons. She was admitted to Royal Woking Hospital on the same day and remained an inpatient for 16 days, being discharged on 27 November 2024.' },
    { heading: 'Scope of This Report', body: 'This report covers the full documented medical timeline from 1992 to 2026. The pre-accident history of multiple sclerosis is treated as background context to establish the claimant\u2019s baseline condition prior to the collision. The principal focus of the report is the collision of 11 November 2024 and its consequences, including the acute neurosurgical and orthopaedic management during the inpatient admission, post-discharge community physiotherapy, fracture clinic follow-up, and psychological intervention for post-traumatic symptoms, together with ongoing neurology and urology reviews documented through to 2026.' },

    { heading: 'Chronological Summary \u2014 Pre-Accident Medical History (1992 to November 2024)', body: 'Jane Doe, born 1 May 1965, was diagnosed with clinically definite multiple sclerosis in 1992 following two distinct neurological episodes. The first was a painful loss of vision in the right eye over several days, accompanied by impaired colour vision and a right relative afferent pupillary defect, which improved partially after oral steroids. The second episode, occurring several months later, comprised ascending numbness to the trunk with sensory disturbance and Lhermitte\u2019s phenomenon, consistent with a partial myelitis. MRI of the brain and cervical cord at that time demonstrated multiple small ovoid periventricular lesions oriented perpendicular to the lateral ventricles, mild swelling of the right optic nerve segment, and a short segment of T2 signal change in the posterior cervical cord at approximately C3 to C4. Visual evoked responses were delayed on the right. Taken together, the clinical and imaging findings supported a diagnosis of clinically definite multiple sclerosis. She has never received disease-modifying therapy at any point during her disease course.' },
    { heading: '', body: 'In June 1996, she was admitted with an acute brainstem relapse characterised by one week of double vision on rightward gaze and oscillopsia. Examination revealed a right sixth cranial nerve palsy and a possible right internuclear ophthalmoplegia. She received two days of intravenous methylprednisolone 1 gram daily, after which the diplopia significantly improved and the sixth nerve palsy was noted to be resolving. She was discharged with outpatient follow-up arranged.' },
    { heading: '', body: 'In March 2000, she was admitted with a five-day history of worsening left leg weakness and reduced walking distance. No clear infective trigger was identified. She received intravenous methylprednisolone 1 gram daily for three days with good recovery. A further episode of worsened gait imbalance and sensory disturbance in early 2008 was treated with steroids arranged by her general practitioner, with only partial recovery.' },
    { heading: '', body: 'By September 2009, her neurologist formally documented transition to secondary progressive multiple sclerosis, characterised by gradual accrual of disability without discrete relapse over the preceding eighteen months. At that time she walked independently indoors and used a stick outdoors with a walking tolerance of approximately 500 metres, and her Expanded Disability Status Scale score was estimated at 4.5. Bladder urgency and occasional urge leakage were documented from this point onwards.' },
    { heading: '', body: 'Over the following years, her mobility progressively declined. By October 2010 she was using one stick indoors on worse days and two sticks outdoors, with an estimated EDSS of 5.0. By October 2011, having retired from nursing on health grounds, she was using a stick indoors and a wheeled frame outdoors for approximately 200 metres, with an EDSS of 5.5. An occupational health assessment in February 2010 had concluded that she was no longer able to meet the physical demands of ward-based nursing safely or reliably, and ill-health retirement was supported.' },
    { heading: '', body: 'A repeat MRI of the brain and cervical spine in September 2004 had demonstrated interval increase in periventricular and juxtacortical T2 lesion burden compared with the 1992 examination, with additional small infratentorial lesions and a chronic non-enhancing cervical cord lesion at C3 to C4.' },
    { heading: '', body: 'Essential hypertension was diagnosed in July 2014 and amlodipine 5 mg once daily was commenced. Depression was diagnosed in October 2015 in the context of marital breakdown and chronic disability. Sertraline was initiated at 50 mg daily and subsequently increased to 100 mg daily. A course of cognitive behavioural therapy was completed through IAPT services. An attempt to reduce sertraline in 2017 resulted in a significant dip in mood, and long-term continuation was thereafter planned.' },
    { heading: '', body: 'Mild cognitive complaints, principally word-finding difficulty and reduced concentration particularly when fatigued, were first documented at the neurology clinic in October 2018. Neurogenic bladder symptoms, including urgency, urge leakage, nocturia, and incomplete emptying, were formally assessed by urology from November 2019. Uroflowmetry and post-void residual assessment at that time demonstrated post-void residual volumes of approximately 160 to 190 millilitres. Solifenacin was prescribed with partial symptomatic benefit. A specialist continence clinic review in July 2021 documented urgency, leakage, nocturia, and incomplete emptying, with constipation identified as an aggravating factor. The first intravesical botulinum toxin injection was performed in September 2022 and was described by the patient as life-changing in terms of confidence leaving the house. A repeat procedure was performed in March 2024.' },
    { heading: '', body: 'By October 2024, at her final neurology clinic review before the collision, her walking tolerance was approximately 50 metres with a wheeled frame, with a mobility scooter used for longer community distances. Her EDSS was estimated at 6.5. She was living alone in a two-storey property, with her adult daughter living locally and providing practical support. Her current medications at that time included amlodipine 5 mg once daily, losartan 50 mg once daily, sertraline 100 mg once daily, baclofen 10 mg three times daily, gabapentin 300 mg three times daily, vitamin D, and solifenacin, though she reported relying principally on the bladder botulinum toxin for urinary symptom control. She remained an active smoker at approximately 10 cigarettes per day despite repeated cessation advice. Sit-to-stand transfers were independent, albeit slow.' },
    { heading: '', body: 'An MS nurse annual review conducted on 9 November 2024, two days before the collision, confirmed this functional baseline and documented ongoing daily fatigue managed with structured rests, variable falls and near-falls, spasticity, leg stiffness, neuropathic discomfort, and bladder disturbance.' },

    { heading: 'The Road Traffic Collision \u2014 11 November 2024', body: 'At approximately 15:02 on 11 November 2024, Jane Doe was involved in a rear-end road traffic collision on the A247 near Send, Surrey. She was the driver of a stationary or slow-moving vehicle that was struck from behind at a reported speed of approximately 35 miles per hour. She was wearing a seatbelt and the airbags did not deploy. A head strike against the headrest and/or side window was documented. A brief loss of consciousness of approximately three to four minutes was witnessed at the scene.' },
    { heading: '', body: 'On arrival of the ambulance crew, she was seated in the vehicle, alert but amnesic for the impact and repeatedly asking what had happened. She complained of headache, neck pain, upper back pain, and severe right lateral hip pain. She was unable to stand, against her known baseline of mobilising approximately 50 metres with a walking frame.' },
    { heading: '', body: 'The primary survey confirmed a patent airway, unlaboured breathing, stable circulation, and no external haemorrhage. A cervical collar was applied and she was extricated using a scoop stretcher. Intravenous paracetamol and small aliquots of morphine were administered, achieving modest pain improvement from 8 out of 10 to 7 out of 10. The first recorded observations at 15:10 showed a Glasgow Coma Scale of 13 (E3 V4 M6), blood pressure 146/88 mmHg, heart rate 92 beats per minute, respiratory rate 18, and oxygen saturation 98% on room air. Subsequent observations at 15:18 and 15:28 showed the GCS improving to 14, with blood pressure and heart rate gradually settling. She was transported to Royal Woking Hospital Major Emergency Department under a trauma pre-alert.' },

    { heading: 'Acute Investigations on Admission', body: 'CT imaging of the head and cervical spine was performed at 17:18 on 11 November 2024. This demonstrated an acute left frontoparietal convexity subdural haematoma measuring up to 4 mm in maximal thickness with trace extension along the falx, without midline shift, herniation, or hydrocephalus. A non-depressed linear fracture involving the right parietal bone extending inferiorly towards the squamous temporal bone was identified, with overlying right parietal scalp swelling. Acute non-displaced spinous process fractures at C7 and T1 were also demonstrated, with associated posterior soft tissue stranding. A further minimally displaced T2 spinous process fracture was identified on the CT of the chest, abdomen, and pelvis performed at 17:22, which otherwise showed no pneumothorax, pulmonary contusion, solid organ laceration, or free fluid.' },
    { heading: '', body: 'MRI of the pelvis and right hip was performed on 13 November 2024. This demonstrated no occult femoral neck, intertrochanteric, or acetabular fracture. Marrow oedema was present at the right greater trochanter with high signal and partial thickness tearing at the insertions of the gluteus medius and minimus tendons, together with associated trochanteric bursitis and peritrochanteric soft tissue oedema. The left hip was unremarkable.' },

    { heading: '16-Day Inpatient Admission at Royal Woking Hospital (11 to 27 November 2024)', body: 'She was formally admitted at 21:35 on 11 November 2024 under the trauma and orthopaedic team with neurosurgical advice. On arrival to the ward, her Glasgow Coma Scale was 15, she had a mild headache without vomiting, focal tenderness over the posterior lower cervical and upper thoracic spinous processes, and marked right greater trochanteric tenderness with pain on any attempted weight bearing. Chronic spastic paraparesis in the lower limbs, left worse than right, was noted as consistent with her known MS baseline.' },
    { heading: '', body: 'A neurosurgical consultation was conducted on 12 November 2024 by telephone and image review. The repeat CT head performed that morning was reviewed and confirmed a stable subdural haematoma of approximately 4 mm without midline shift, new haemorrhage, or neurological deterioration. The Glasgow Coma Scale remained at 15. Conservative management was recommended, comprising continued neurological observations for 24 hours and no neurosurgical intervention. The plan was for a further repeat CT head at 48 to 72 hours if clinically indicated.' },
    { heading: '', body: 'Ward round documentation on 12 November 2024 noted the stable CT findings, improved headache, and inability to stand due to right hip pain. An MRI of the hip was requested. On 13 November 2024, an orthopaedic consultation was conducted in response to persistent severe right lateral hip pain and inability to weight bear despite the absence of fracture on CT. The MRI findings of marrow oedema at the greater trochanter with partial tearing of the gluteus medius and minimus insertions and associated trochanteric bursitis were reviewed. A working diagnosis of post-traumatic right greater trochanteric pain syndrome with abductor mechanism injury was established, and conservative management was recommended comprising protected weight bearing as tolerated with a frame, analgesia, and physiotherapy.' },
    { heading: '', body: 'The cervical collar was removed by 16 November 2024 as neck pain settled. A bowel regimen with macrogol was commenced on 14 November 2024 in response to opioid-related constipation. Analgesia comprised regular paracetamol with oxycodone as needed.' },
    { heading: '', body: 'Ward round entries through the admission documented a stepwise functional recovery. On 14 November 2024, pain remained significant during transfers. By 15 November 2024, a two-person assisted transfer to a chair had been achieved, though walking had not yet been accomplished. On 16 November 2024, mobilisation remained limited by lateral hip pain and fear of falling, and pre-medication before therapy sessions was introduced to facilitate rehabilitation participation. On 17 November 2024, a few steps with a wheeled frame under heavy supervision were achieved; low mood and poor sleep were also documented at this point, with reassurance provided regarding the stable CT head findings. Formal discharge planning commenced on 18 November 2024, with anticipated needs identified as a downstairs living setup, commode provision, a package of care with carers, and ongoing community rehabilitation.' },
    { heading: '', body: 'The initial physiotherapy assessment was conducted on 19 November 2024. Pre-collision baseline mobility was documented as approximately 50 metres with a wheeled frame and scooter for community distances, with independent transfers at home. Current functional status at assessment was recorded as unable to step away from the bedside due to severe right lateral hip pain and fear of buckling. A multidisciplinary social work and occupational therapy planning meeting on 20 November 2024 identified risks including falls, inability to access the toilet in time, low mood, and reduced confidence. The patient expressed a strong preference for discharge home with support rather than residential rehabilitation, and the team agreed this was reasonable once basic transfers and five metres of mobility were achieved.' },
    { heading: '', body: 'Physiotherapy sessions on 21 November 2024 documented that she stood with minimal assistance and walked two metres with a wheeled frame, with a marked antalgic pattern and reduced right single-leg stance. By 23 November 2024, she was transferring with contact guard only and walked four metres with the frame. On 25 November 2024, the discharge criterion of approximately five metres with a wheeled frame was achieved. She was assessed as not safe for stairs at that point.' },
    { heading: '', body: 'She was discharged on 27 November 2024 following a 16-day admission. A package of care was in place with carers attending four times daily, a downstairs hospital bed and commode had been set up at her home, community physiotherapy had been arranged, and her daughter was providing daily support.' },

    { heading: 'Post-Discharge Recovery (November 2024 to March 2026)', body: 'A home visit was conducted by her general practitioner two days after discharge, on 29 November 2024. She was mobilising only a few metres with the frame, significant right hip pain persisted, headaches were improving, and there were no vomiting or focal neurological signs. Sertraline was continued and the oxycodone dose was being tapered due to constipation.' },
    { heading: '', body: 'A telephone consultation on 12 December 2024 documented that she was still walking approximately five metres with the frame, experiencing poor sleep and crying spells, and reporting feeling on edge since the crash with panic as a passenger. Oxycodone had been nearly stopped due to constipation, and paracetamol was continued.' },
    { heading: '', body: 'The MS nurse annual review on 13 December 2024 confirmed a significant functional decline compared with the pre-collision baseline, with stairs remaining a major obstacle and the patient requiring help from her daughter for shopping, heavier domestic tasks, and some appointments.' },
    { heading: '', body: 'The first orthopaedic follow-up clinic appointment after discharge took place on 19 December 2024. The patient remained very limited, able to walk only a few metres with the frame. Marked tenderness over the right greater trochanter and abductor insertion was present without groin pain. The MRI pattern was confirmed as consistent with abductor mechanism injury and conservative management was continued.' },
    { heading: '', body: 'Community physiotherapy commenced with an initial home assessment on 8 January 2025. At that time she was managing bed to commode and chair transfers with close supervision and walking approximately five to six metres with the wheeled frame. A marked antalgic gait pattern was documented, with poor right single limb support, baseline left leg weakness from MS, and significant fear of falling.' },
    { heading: '', body: 'On 21 January 2025, a general practice consultation documented intrusive recollections, hypervigilance in cars, an exaggerated startle response, poor sleep, and avoidance of busier roads. Sertraline was increased from 100 mg to 150 mg daily and a referral for trauma-focused cognitive behavioural therapy was made.' },
    { heading: '', body: 'A further orthopaedic follow-up on 30 January 2025 documented modest improvement, with walking distance now approximately eight to ten metres indoors with the frame. Pain remained laterally based and was aggravated by single-leg stance and transfers.' },
    { heading: '', body: 'By the orthopaedic review of 27 March 2025, walking distance had improved to approximately 20 to 25 metres with the frame on a good day, though stairs remained unmanageable and transport to appointments continued to depend on her daughter.' },
    { heading: '', body: 'A community physiotherapy progress review on 17 April 2025 confirmed walking of approximately 25 metres with the frame on a good day and transfers with supervision only. Ongoing issues included lateral hip pain with fatigue, reduced confidence, and chronic MS-related spasticity causing an asymmetrical gait.' },
    { heading: '', body: 'At the six-month orthopaedic review on 22 May 2025, walking distance had improved to approximately 35 to 40 metres with the frame, approaching but not yet reaching the pre-collision baseline of 50 metres. She was discharged from routine orthopaedic clinic follow-up at that appointment, with an open appointment retained should symptoms worsen.' },
    { heading: '', body: 'A community occupational therapy falls risk and home adaptations review was conducted on 24 June 2025. The downstairs living arrangement was continuing because stairs remained difficult and confidence was poor. She was independent with most seated self-care but continued to benefit from her daughter\u2019s assistance with shopping, laundry, and some meal preparation.' },
    { heading: '', body: 'The neurology clinic review of 21 October 2025, one year after the collision, documented that following the November 2024 collision she had initially dropped to only a few metres with the frame and had by autumn 2025 recovered to 30 to 40 metres, still below her pre-collision baseline. Low mood and anxiety were documented as ongoing, with the EDSS estimated at 6.5 to 7.0.' },
    { heading: '', body: 'A urology consultation on 11 September 2025 confirmed that repeat intradetrusor botulinum toxin treatment continued to control urgency and leakage well, though incomplete emptying was becoming more pronounced. Intermittent self-catheterisation was discussed.' },
    { heading: '', body: 'The MS nurse annual review of 9 January 2026 documented that she could walk approximately 40 to 50 metres with the frame on good days, with community trips still requiring the scooter or wheelchair. Confidence remained lower than before the collision and stairs were still largely avoided.' },
    { heading: '', body: 'A urology consultation on 26 February 2026 confirmed that intermittent self-catheterisation had been established and mastered under specialist nurse supervision, using a 10 Charri\u00e8re catheter four times daily, with clear improvement in confidence and fewer urgency accidents.' },
    { heading: '', body: 'A general practice medication review and chronic disease management consultation on 18 March 2026 confirmed that the sertraline dose had been self-reduced by the patient from 150 mg back to 100 mg daily over the preceding months as she reported coping better. Current medications at that time were amlodipine 5 mg once daily, losartan 50 mg once daily, sertraline 100 mg once daily, baclofen 10 mg three times daily, gabapentin 300 mg three times daily, vitamin D, and macrogol as needed.' },
    { heading: '', body: 'The neurology clinic review of 14 October 2026, two years after the collision, documented walking of 40 to 50 metres with the frame on a good day, with confidence still below pre-accident levels and stairs largely avoided. Mild cognitive difficulty comprising attention and short-term memory impairment under fatigue was documented. Bladder management continued with intradetrusor botulinum toxin and intermittent self-catheterisation. The EDSS was estimated at 6.5 to 7.0. Ongoing neurology and urology reviews were documented as continuing into 2026.' },

    { heading: 'Key Observations \u2014 The Road Traffic Collision as a Discrete Event Against a Stable Baseline', body: 'A particularly significant feature of this case is the availability of contemporaneous clinical records documenting the claimant\u2019s functional status in the weeks immediately before the collision. Both a neurology clinic review and an MS nurse annual review were conducted in October and early November 2024 respectively, establishing a clear and well-evidenced pre-collision baseline: independent transfers, approximately 50 metres of walking with a wheeled frame, and scooter use for longer distances. This proximity of pre-collision assessments to the index event on 11 November 2024 means that the functional drop attributable to the collision can be measured against a reliable and recently recorded starting point, rather than estimated retrospectively.' },

    { heading: 'Severity and Multiplicity of Injuries', body: 'The records document that a single collision resulted in four distinct injury types: an acute intracranial haemorrhage (left frontoparietal subdural haematoma), a skull fracture (right parietal linear fracture), multiple spinal bony injuries (non-displaced spinous process fractures at C7, T1, and T2), and a severe soft tissue injury to the right hip (partial tearing of gluteus medius and minimus tendons with trochanteric bursitis). The combination of head, spinal, and hip injuries in a patient with pre-existing lower limb weakness and balance impairment from secondary progressive multiple sclerosis is a notable feature of the clinical picture. The hip injury in particular \u2014 affecting the abductor mechanism on the right side \u2014 imposed a direct and compounding burden on a patient whose left lower limb was already the weaker side from MS, leaving her with bilateral lower limb compromise during the recovery period.' },

    { heading: 'The Acute Admission: Rapid Neurosurgical Decision-Making', body: 'The records show that neurosurgical consultation was obtained promptly, with image review and telephone assessment completed on 12 November 2024, the day after admission. The decision to manage the subdural haematoma conservatively was supported by a repeat CT head demonstrating stability, the absence of midline shift, and a Glasgow Coma Scale of 15. The records do not document any subsequent neurosurgical deterioration, and headaches are recorded as resolving by early 2025. There is no documented follow-up neuroimaging after the repeat CT of 12 November 2024, which may be relevant if any later cognitive or neurological symptoms require consideration in the context of the head injury.' },

    { heading: 'Physiotherapy Progress During Admission', body: 'The inpatient physiotherapy records chart a clear trajectory from complete inability to stand on admission, through two metres with a frame on 21 November 2024, to approximately five metres with a frame by 25 November 2024. The threshold of five metres was identified as the criterion for safe discharge home. It is notable that pre-medication before physiotherapy sessions was required to enable participation, reflecting the severity of pain from the hip injury. The discharge mobility of five metres represented a significant reduction from the pre-collision baseline of 50 metres, and the records document that the patient was not safe for stairs at the point of discharge.' },

    { heading: 'Post-Discharge Recovery: Incomplete Return to Baseline', body: 'A consistent pattern emerges across the post-discharge records. At each review point \u2014 orthopaedic clinic, community physiotherapy, GP, and neurology \u2014 the claimant\u2019s walking distance is documented as progressively improving but persistently below her pre-collision baseline. By six months post-collision (May 2025), she was managing approximately 35 to 40 metres with a frame, described in the orthopaedic records as approaching but not yet fully back to the pre-collision status of 50 metres. By early 2026, the records indicate a recovery to approximately 40 to 50 metres on good days, which broadly approaches the pre-collision baseline, though with reduced confidence and continued avoidance of stairs. The records consistently note that stairs remain a major obstacle and that the claimant continues to live in a downstairs arrangement that was not in place before the collision. This represents a lasting change to her living arrangements that is not reflected by walking distance figures alone.' },

    { heading: 'Psychological Sequelae: A Delayed but Documented Trajectory', body: 'Low mood and tearfulness are documented from the first week of the inpatient admission. However, the records show that formal recognition of post-traumatic stress symptoms \u2014 including intrusive recollections, hypervigilance in cars, exaggerated startle response, and avoidance of busier roads \u2014 did not occur until the GP consultation of 21 January 2025, approximately ten weeks after the collision. Sertraline was increased at that point and a referral for trauma-focused cognitive behavioural therapy was made. The records do not document the outcome of that referral or confirm whether trauma-focused therapy was received and completed, which represents a gap in the available documentation. The claimant had a pre-existing history of depression treated with sertraline from 2015, and the records show that the sertraline dose was increased above its pre-collision level following the collision and subsequently reduced again by March 2026 as mood improved. The interaction between pre-existing depression and post-collision psychological symptoms is acknowledged in the records but not formally disentangled.' },

    { heading: 'Bladder Management: A Separate Trajectory', body: 'The records relating to neurogenic bladder management reflect a clinical course that appears to have continued largely independently of the collision. Intra-detrusor Botox injections were already established before the collision, with a repeat procedure performed in March 2024. The urology review of September 2025 and the February 2026 consultation document the establishment of intermittent self-catheterisation, which represents a significant escalation in bladder management. The records do not explicitly attribute this escalation to the collision, and the progression of incomplete bladder emptying is consistent with the documented natural history of neurogenic bladder in secondary progressive MS. However, the records also note that during the acute post-collision period, the claimant was unable to reach the toilet in time due to the combination of reduced mobility and urinary urgency, which is a functionally significant observation.' },

    { heading: 'Record Gaps and Inconsistencies', body: 'One minor inconsistency is present in the spinal injury documentation. The initial CT report of 11 November 2024 records acute non-displaced spinous process fractures at C7 and T1, while the CT chest, abdomen, and pelvis performed on the same date additionally identifies a minimally displaced T2 spinous process fracture. The admission clerking and subsequent records refer to fractures at C7, T1, and T2, suggesting the T2 fracture was incorporated into the working diagnosis, but the discrepancy between the two CT reports is not explicitly reconciled in the records.' },
    { heading: '', body: 'A further gap concerns the absence of any formal neuropsychological assessment. Mild cognitive complaints \u2014 word-finding difficulty, attention, and short-term memory under fatigue \u2014 were documented from 2018 as part of the MS disease course. Following the collision, similar symptoms are noted in the records, but no formal cognitive testing is documented at any point before or after the collision. This makes it difficult to determine from the records alone whether any cognitive change occurred as a consequence of the head injury, or whether the documented symptoms represent a continuation of the pre-existing MS-related cognitive picture.' },
    { heading: '', body: 'Finally, the records confirm that the claimant has never received disease-modifying therapy for her multiple sclerosis across the entire disease course from 1992 to 2026. This is a consistent and repeatedly documented feature of her MS management. The records do not document the reasons for this, and no record of a formal discussion about disease-modifying therapy eligibility or patient preference is identifiable within the bundle.' },
  ];
  const reportFullText = reportSections.map(s => s.heading + s.body).join('');
  const reportTotalChars = reportFullText.length;

  const featureTiles = [
    { icon: Clock, label: 'Structured Chronologies', desc: 'Structured narratives from raw clinical records', colour: 'primary' },
    { icon: FileText, label: 'AI-Generated Reports', desc: 'Full reports drafted in seconds, not hours', colour: 'primary' },
    { icon: LucideLink, label: 'Every Claim Source-Linked', desc: 'Click any statement to see the original record', colour: 'amber' },
    { icon: Layers, label: 'Any Document, Any Format', desc: 'Scanned PDFs, handwritten notes, typed letters', colour: 'primary' },
    { icon: Download, label: 'Export-Ready for Court Bundles', desc: 'Download to Word or PDF, formatted and cited', colour: 'primary' },
    { icon: MessageSquare, label: 'Case Assistant', desc: 'Ask questions about your case and get cited, contextual answers', colour: 'primary' },
  ];

  const screenCount = 9;
  const screenLabels = ['Intro', 'Records', 'Overview', 'Chronology', 'Reports', 'Bundles', 'Standards', 'Security', 'Demo'];
  const screenCaptions = [
    'ClinLexis™ uses AI to turn thousands of pages of medical records into a structured, citable case file.',
    'Upload case medical records including handwritten letters.',
    'A structured case overview, fully cited.',
    ctaVariant === 'booth'
      ? 'Search or filter the full chronology in plain English. Ask the AI Case Assistant anything.'
      : 'A full narrative chronology you can search or filter in plain English. Or ask the AI Case Assistant anything about your records.',
    ctaVariant === 'booth'
      ? 'Letters and reports, cited and drafted in seconds. Pick a specialism template, or bring your own.'
      : 'Letters and reports that used to take days, drafted automatically. Choose from dozens of specialism templates — or bring your own. ClinLexis™ does the rest.',
    'Solicitors can automatically create a Bates-stamped paginated bundle of every record, generated and shared in a single click.',
    'The ClinLexis™ AI can compare treatment to accepted clinical standards, build the causation argument, and uncover what\u2019s missing or inconsistent in the records.',
    'Enterprise-grade security. UK data residency. GDPR compliant.',
    ctaVariant === 'signup'
      ? 'ClinLexis™ gives you back the hours you spend on paperwork. Try it on your next case — £24.99/month, 1,000 complimentary pages, consumption-based thereafter.'
      : ctaVariant === 'booth'
      ? 'Welcome to our stand. Scan for a conference-only trial or to book a personalised demo at clinlexis.co.uk/conference.'
      : 'ClinLexis™ gives you back the hours you spend on paperwork. Book a free 30-minute demo at clinlexis.co.uk to see how it can transform your case preparation.',
  ];

  // Booth-mode captions: rendered statically with typographic emphasis instead of audio-driven karaoke,
  // since the booth TV plays silent. Bold carries the meaning; line breaks let glance-readers parse the structure fast.
  const boothCaptions: Record<number, React.ReactNode> = {
    0: <><strong className="font-extrabold">Thousands of pages</strong> of medical records,<br/>turned into a <strong className="font-extrabold">structured, citable case file</strong>.</>,
    1: <><strong className="font-extrabold">Upload anything.</strong> Even <strong className="font-extrabold">handwritten letters</strong>.</>,
    2: <>A <strong className="font-extrabold">structured case overview</strong>, every claim <strong className="font-extrabold">cited to source</strong>.</>,
    3: <><strong className="font-extrabold">Search and filter</strong> the chronology in plain English.<br/><strong className="font-extrabold">Ask anything</strong> of the AI Case Assistant.</>,
    4: <><strong className="font-extrabold">Letters and reports</strong>, <strong className="font-extrabold">cited and drafted in seconds</strong>.<br/>Pick a <strong className="font-extrabold">specialism template</strong>, or <strong className="font-extrabold">bring your own</strong>.</>,
    5: <><strong className="font-extrabold">Bates-stamped paginated bundles</strong>,<br/>built and shared in <strong className="font-extrabold">one click</strong>.</>,
    6: <>Compare to <strong className="font-extrabold">clinical standards</strong>. Build <strong className="font-extrabold">causation</strong>.<br/>Surface what's <strong className="font-extrabold">missing or inconsistent</strong>.</>,
    7: <><strong className="font-extrabold">Enterprise-grade security</strong>. <strong className="font-extrabold">UK data residency</strong>. <strong className="font-extrabold">GDPR compliant</strong>.</>,
    8: <><strong className="font-extrabold">Welcome to our stand.</strong><br/>Scan for a <strong className="font-extrabold">conference-only trial</strong> or <strong className="font-extrabold">personalised demo</strong>.</>,
  };

  const nextScreen = useCallback(() => {
    setActiveScreen((prev) => (prev + 1) % screenCount);
  }, []);

  // File drop + processing animation for screen 1 (Records)
  useEffect(() => {
    if (activeScreen === 1) {
      setFilesRevealed(0);
      setFileStatuses(['Processing', 'Processing', 'Processing', 'Processing']);
      setUploadProgress(0);
      setUploadPhase('dropping');

      const files = [0, 1, 2, 3];
      const filePages = [312, 287, 156, 92];
      const totalPages = 847;
      let cumulativePages = 0;

      // Drop files in one by one
      files.forEach((i) => {
        setTimeout(() => {
          setFilesRevealed(i + 1);
        }, 500 + i * 500);
      });

      // After all files dropped, start processing them sequentially
      const processingStart = 500 + 4 * 500 + 300; // after last file drops + small pause
      files.forEach((i) => {
        const delay = processingStart + i * 800;
        setTimeout(() => {
          setUploadPhase('processing');
          cumulativePages += filePages[i];
          const pct = Math.round((cumulativePages / totalPages) * 100);
          setUploadProgress(pct);
          setFileStatuses((prev) => {
            const next = [...prev];
            next[i] = 'Processed';
            return next;
          });
        }, delay);
      });

      // Mark done
      setTimeout(() => {
        setUploadPhase('done');
        setUploadProgress(100);
      }, processingStart + 4 * 800 + 200);

      return () => {
        setFilesRevealed(0);
        setUploadPhase('dropping');
      };
    }
  }, [activeScreen]);

  // Chronology animation for screen 3:
  //   1. Idle: full chronology shown
  //   2. Click period 1 → ripple → expand raw events
  //   3. Click event 1 citation → typed neurology letter slides in, key sentence highlighted
  //   4. Click event 2 citation → handwritten admission note slides in, key line highlighted
  useEffect(() => {
    if (activeScreen !== 3) return;
    setFilterPhase('idle');
    setTypedChars(0);
    setExpandedPeriod(null);
    setPeriodClickRipple(null);
    setEventClickRipple(null);
    setChronoScrollY(0);

    const timers: ReturnType<typeof setTimeout>[] = [];

    // 600ms: ripple on period 1
    timers.push(setTimeout(() => setPeriodClickRipple(0), 600));
    // 850ms: expand period 1
    timers.push(setTimeout(() => {
      setExpandedPeriod(0);
      setPeriodClickRipple(null);
    }, 850));
    // 2700ms: ripple on event 1 (Diagnosis citation)
    timers.push(setTimeout(() => setEventClickRipple(0), 2700));
    // 2950ms: enter citation-1 (typed neurology letter)
    timers.push(setTimeout(() => {
      setFilterPhase('citation-1');
      setEventClickRipple(null);
    }, 2950));
    // 5400ms: scroll the chronology down (push AT A GLANCE up, bring later events into focus)
    timers.push(setTimeout(() => setChronoScrollY(-90), 5400));
    // 5900ms: ripple on event 2 (Relapse citation)
    timers.push(setTimeout(() => setEventClickRipple(1), 5900));
    // 6150ms: enter citation-2 (handwritten admission note)
    timers.push(setTimeout(() => {
      setFilterPhase('citation-2');
      setEventClickRipple(null);
    }, 6150));

    return () => {
      timers.forEach(clearTimeout);
      setFilterPhase('idle');
      setTypedChars(0);
      setExpandedPeriod(null);
      setPeriodClickRipple(null);
      setEventClickRipple(null);
      setChronoScrollY(0);
    };
  }, [activeScreen]);

  // Report drafting animation for screen 4 (Reports)
  useEffect(() => {
    if (activeScreen !== 4) return;
    // Reset on entry only — leaving the screen should NOT flicker the view back to options
    setReportPhase('options');
    setReportGenChars(0);
    const selectTimeout = setTimeout(() => {
      setReportPhase('selected');
      setTimeout(() => {
        setReportPhase('drafting');
      }, 1000);
    }, 2000);
    return () => {
      clearTimeout(selectTimeout);
    };
  }, [activeScreen]);

  useEffect(() => {
    if (reportPhase === 'drafting') {
      const interval = setInterval(() => {
        setReportGenChars((prev) => {
          if (prev >= reportTotalChars) {
            clearInterval(interval);
            return prev;
          }
          return prev + 15;
        });
      }, 16);
      return () => clearInterval(interval);
    }
  }, [reportPhase, reportTotalChars]);

  // Auto-scroll the report container as content grows
  const reportScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (reportPhase === 'drafting' && reportScrollRef.current) {
      reportScrollRef.current.scrollTop = reportScrollRef.current.scrollHeight;
    }
  }, [reportGenChars, reportPhase]);

  // Intro logo assembly animation for screen 0
  useEffect(() => {
    if (activeScreen !== 0 || !started) {
      setIntroStep(0);
      return;
    }
    const timings = [200, 500, 800, 1200, 1700, 2100, 2500, 2900];
    const timers = timings.map((t, i) =>
      setTimeout(() => setIntroStep(i + 1), t)
    );
    return () => timers.forEach(clearTimeout);
  }, [activeScreen, started]);
  // Booth CTA splash — re-runs the logo assembly on screen 8 to bookend the loop
  useEffect(() => {
    if (activeScreen !== 8 || ctaVariant !== 'booth' || !started) {
      setBoothSplashStep(0);
      return;
    }
    const timings = [150, 350, 550, 800, 1200, 1500, 1900];
    const timers = timings.map((t, i) =>
      setTimeout(() => setBoothSplashStep(i + 1), t)
    );
    return () => timers.forEach(clearTimeout);
  }, [activeScreen, ctaVariant, started]);

  // Paginated bundle generation animation for screen 5
  useEffect(() => {
    if (activeScreen !== 5) {
      setBundlePhase('idle');
      setBundleProgress(0);
      setBundleScroll(0);
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    const intervals: ReturnType<typeof setInterval>[] = [];

    // Phase 1: Start building after brief delay
    timers.push(setTimeout(() => setBundlePhase('building'), 400));

    // Progress bar fills over ~2s
    intervals.push(setInterval(() => {
      setBundleProgress((p) => {
        const next = p + 4;
        if (next >= 100) return 100;
        return next;
      });
    }, 80));

    // Phase 2: Show ready state briefly
    timers.push(setTimeout(() => setBundlePhase('ready'), 2700));

    // Phase 3: Open PDF viewer and start scrolling
    timers.push(setTimeout(() => setBundlePhase('viewing'), 3400));

    // Scroll the PDF — slow continuous scroll
    timers.push(setTimeout(() => {
      const scrollInterval = setInterval(() => {
        setBundleScroll((s) => {
          const next = s + 0.6;
          if (next >= 100) {
            clearInterval(scrollInterval);
            return 100;
          }
          return next;
        });
      }, 50);
      intervals.push(scrollInterval);
    }, 3700));

    return () => {
      timers.forEach(clearTimeout);
      intervals.forEach(clearInterval);
    };
  }, [activeScreen]);

  // Tile highlight progression for screen 5 (Paginated Bundle)
  useEffect(() => {
    if (activeScreen !== 5) {
      setBundleHighlight(-1);
      return;
    }
    const timers = [
      setTimeout(() => setBundleHighlight(0), 800),
      setTimeout(() => setBundleHighlight(1), 3000),
      setTimeout(() => setBundleHighlight(2), 5500),
    ];
    return () => timers.forEach(clearTimeout);
  }, [activeScreen]);

  // Tile highlight progression for screen 6 (Objective Analysis)
  useEffect(() => {
    if (activeScreen !== 6) {
      setAnalysisHighlight(-1);
      return;
    }
    const timers = [
      setTimeout(() => setAnalysisHighlight(0), 800),
      setTimeout(() => setAnalysisHighlight(1), 2700),
      setTimeout(() => setAnalysisHighlight(2), 4600),
    ];
    return () => timers.forEach(clearTimeout);
  }, [activeScreen]);

  // Overview page scroll animation for screen 2
  useEffect(() => {
    if (activeScreen !== 2) return;
    // Reset to top only when entering screen 2 (avoids jolt during fade-out to next screen)
    setOverviewScroll(0);
    // Measure the content vs container so the scroll lands at the actual bottom, not past it
    const measureTimer = setTimeout(() => {
      const content = overviewContentRef.current;
      const container = content?.parentElement;
      if (!content || !container) return;
      const max = Math.max(0, content.scrollHeight - container.clientHeight);
      setOverviewMaxScroll(max);
    }, 50);
    const holdTimer = setTimeout(() => {
      const interval = setInterval(() => {
        setOverviewScroll((s) => {
          const next = s + 2.0;
          if (next >= 100) {
            clearInterval(interval);
            return 100;
          }
          return next;
        });
      }, 50);
      (holdTimer as unknown as { _interval?: ReturnType<typeof setInterval> })._interval = interval;
    }, 400);
    return () => {
      clearTimeout(measureTimer);
      clearTimeout(holdTimer);
      const inner = (holdTimer as unknown as { _interval?: ReturnType<typeof setInterval> })._interval;
      if (inner) clearInterval(inner);
    };
  }, [activeScreen, ctaVariant]);

  // Auto-advance carousel — waits for audio to finish, then holds 1.5s before advancing.
  // Falls back to animation-based durations if no audio is available.
  useEffect(() => {
    if (isPaused || !started) return;
    const audio = audioRefs.current[activeScreen];
    // Stop auto-advancing on the CTA screen — except in booth mode, where the loop continues back to intro after a hold.
    if (activeScreen === 8 && ctaVariant !== 'booth') return;
    const minByScreen: Record<number, number> = { 0: 6000, 1: 6500, 2: 2500, 3: 8500, 4: 11300, 5: 9000, 6: 7000, 7: 6000, 8: 12000 };
    const animationMin = minByScreen[activeScreen] ?? 6000;

    const breathingRoom = activeScreen === 2 ? 1200 : 500;
    // Screen 0 audio starts with a 1s delay, so add that to the timer
    const audioStartDelay = activeScreen === 0 ? 1000 : 0;

    const scheduleAdvance = (audioDuration: number) => {
      const audioDurationMs = audioDuration * 1000 + breathingRoom + audioStartDelay;
      const duration = Math.max(audioDurationMs, animationMin);
      return setTimeout(nextScreen, duration);
    };

    // If audio is loaded and has a valid duration, use whichever is longer: audio or animation
    if (audio && audio.duration && isFinite(audio.duration)) {
      const timer = scheduleAdvance(audio.duration);
      return () => clearTimeout(timer);
    }

    // Audio not ready yet — listen for it to load, otherwise fall back
    let timer: ReturnType<typeof setTimeout>;
    const onLoaded = () => {
      timer = scheduleAdvance(audio.duration);
    };
    if (audio) {
      audio.addEventListener('loadedmetadata', onLoaded, { once: true });
    }
    // Fallback if audio never loads
    const fallback = setTimeout(nextScreen, animationMin);
    return () => {
      clearTimeout(timer);
      clearTimeout(fallback);
      if (audio) audio.removeEventListener('loadedmetadata', onLoaded);
    };
  }, [activeScreen, nextScreen, isPaused, started, ctaVariant]);

  // Banner mode — render a roller-banner-formatted layout (1:2 portrait, 1000mm x 2000mm)
  if (ctaVariant === 'banner1' || ctaVariant === 'banner2') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-medical-100 p-6">
        {/* Print isolation — only the banner prints, scaled to fill 1000mm x 2000mm */}
        <style>{`
          @page {
            size: 1000mm 2000mm;
            margin: 0;
          }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              overflow: hidden !important;
            }
            body * { visibility: hidden !important; }
            #banner-export-target, #banner-export-target * { visibility: visible !important; }
            #banner-export-target {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              width: 500px !important;
              height: 1000px !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              transform-origin: 0 0 !important;
              transform: scale(7.559) !important;
            }
          }
        `}</style>
        {/* View toggle stays visible */}
        <div
          className="fixed top-4 right-4 z-[60] bg-white border border-medical-200 rounded-full shadow-md p-1 flex items-center gap-1 text-xs"
        >
          <span className="px-2 text-[10px] font-semibold text-medical-500 uppercase tracking-wide">View:</span>
          {([
            { key: 'demo', label: 'Demo' },
            { key: 'signup', label: 'Sign-up' },
            { key: 'booth', label: 'Booth' },
            { key: 'banner1', label: 'Banner 1' },
            { key: 'banner2', label: 'Banner 2' },
          ] as const).map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setCtaVariant(opt.key)}
              className={`px-3 py-1 rounded-full transition-colors ${
                ctaVariant === opt.key ? 'bg-primary-600 text-white' : 'text-medical-600 hover:bg-medical-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* The banner — fixed 1:2 aspect ratio matching 1000mm x 2000mm at print */}
        <div
          className="bg-white shadow-2xl"
          style={{
            width: '500px',
            height: '1000px',
            position: 'relative',
            overflow: 'hidden',
            aspectRatio: '1 / 2',
          }}
          id="banner-export-target"
        >
          {ctaVariant === 'banner1' ? (
            /* BANNER 1 — Hero / Value prop */
            <div className="w-full h-full flex flex-col bg-gradient-to-b from-white via-white to-primary-50/40">
              {/* Logo header */}
              <div className="pt-12 flex justify-center">
                <Logo size="xl" />
              </div>

              {/* Hero text — vertically centered in whitespace between logo and tiles */}
              <div className="flex-1 flex items-center justify-center px-8 text-center">
                <h1 className="text-[34px] font-extrabold text-medical-900 leading-[1.1] tracking-tight">
                  An <span className="text-primary-600">AI evidence-<br />navigation platform</span><br />
                  for UK solicitors and<br />expert witnesses
                </h1>
              </div>

              {/* Capability tiles */}
              <div className="px-10 pb-6 flex flex-col gap-4">
                {[
                  { icon: Layers, label: 'Paginated PDFs, in a click', desc: 'Page-numbered, disclosure-ready PDFs whenever you need them', iconBg: 'bg-emerald-100', tileBg: 'bg-emerald-50/40', border: 'border-emerald-200/60', icon_colour: 'text-emerald-600' },
                  { icon: Clock, label: 'Structured Chronologies', desc: 'Every event organised in a detailed chronology and cited back to source', iconBg: 'bg-amber-100', tileBg: 'bg-amber-50/40', border: 'border-amber-200/60', icon_colour: 'text-amber-600' },
                  { icon: MessageSquare, label: 'AI Case Assistant', desc: 'Intelligent interrogation of case material, supporting your professional judgement', iconBg: 'bg-violet-100', tileBg: 'bg-violet-50/40', border: 'border-violet-200/60', icon_colour: 'text-violet-600' },
                  { icon: FileText, label: 'Your Reports and Letters, Drafted Faster', desc: 'Drafted automatically, refined by your specialist judgement', iconBg: 'bg-sky-100', tileBg: 'bg-sky-50/40', border: 'border-sky-200/60', icon_colour: 'text-sky-600' },
                ].map((tile) => (
                  <div key={tile.label} className={`flex items-start gap-4 p-4 rounded-xl ${tile.tileBg} border ${tile.border} shadow-sm`}>
                    <div className={`w-12 h-12 rounded-lg ${tile.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <tile.icon className={`w-6 h-6 ${tile.icon_colour}`} strokeWidth={1.6} />
                    </div>
                    <div>
                      <p className="text-base font-bold text-medical-900 leading-tight mb-1">{tile.label}</p>
                      <p className="text-sm text-medical-600 leading-snug">{tile.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Disclaimer */}
              <div className="px-4 py-3 bg-white border-t border-medical-200/80">
                <p className="text-[10px] font-semibold text-medical-600 text-center leading-snug whitespace-nowrap">
                  ClinLexis assists with evidence navigation, not medical or legal opinion
                </p>
              </div>

              {/* Footer */}
              <div className="pt-3 pb-5 text-center bg-primary-600 text-white flex items-center justify-center min-h-[52px]">
                <p className="text-xl font-medium leading-none">clinlexis.co.uk</p>
              </div>
            </div>
          ) : (
            /* BANNER 2 — Pricing / CTA */
            <div className="w-full h-full flex flex-col bg-gradient-to-b from-white via-white to-primary-50/40">
              {/* Logo header */}
              <div className="pt-12 flex justify-center">
                <Logo size="xl" />
              </div>

              {/* Hero text — vertically centered in whitespace between logo and cards */}
              <div className="flex-1 flex items-center justify-center px-10 text-center">
                <h1 className="text-[34px] font-extrabold text-medical-900 leading-[1.1] tracking-tight">
                  From thousands of pages<br />
                  <span className="text-primary-600">to a structured case file</span>
                </h1>
              </div>

              <div className="px-10 pb-6 flex flex-col items-center">
                {/* Conference exclusive — whitepaper + trial */}
                <div className="w-full bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700/80 text-center mb-1">Conference exclusive</p>
                  <p className="text-2xl font-extrabold text-amber-700 mb-3 text-center tracking-tight leading-tight">Try it on your next case</p>
                  <div className="flex items-center gap-4">
                    <img
                      src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Fclinlexis.co.uk%2Fconference&color=1e3a5f&bgcolor=ffffff&qzone=1"
                      alt="QR code to clinlexis.co.uk/conference"
                      className="w-24 h-24 rounded-md border border-amber-200 bg-white flex-shrink-0"
                    />
                    <div className="flex-1">
                      <p className="text-lg font-extrabold text-medical-900 leading-tight mb-1.5 tracking-tight">Limited Trial Offer</p>
                      <p className="text-xs text-medical-700 leading-snug mb-2">Scan to claim a conference-only trial</p>
                      <p className="text-xs font-semibold text-medical-900">clinlexis.co.uk/conference</p>
                    </div>
                  </div>
                </div>

                {/* Value card */}
                <div className="mt-6 w-full bg-primary-50/40 border-2 border-primary-200 rounded-2xl shadow-lg p-6 text-center">
                  <h3 className="text-2xl font-extrabold text-medical-900 leading-tight mb-1">
                    Low monthly fee
                  </h3>
                  <h3 className="text-2xl font-extrabold text-primary-600 leading-tight mb-4">
                    Pay only for what you use
                  </h3>
                  <div className="h-px bg-medical-200 my-4" />
                  <div className="space-y-3 text-left">
                    <div className="flex items-start gap-2.5">
                      <Zap className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" strokeWidth={2} />
                      <p className="text-sm text-medical-800 whitespace-nowrap"><span className="font-bold">Consumption-based pricing</span>, no minimums</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <TrendingUp className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" strokeWidth={2} />
                      <p className="text-sm text-medical-800"><span className="font-bold">Scales with your case volume</span>, not a fixed seat</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compliance & Security pill — matches AI Case Assistant inside, darker border */}
              <div className="mx-10 mb-6 bg-violet-50/40 border-2 border-violet-300 rounded-2xl p-6">
                <p className="text-xl font-extrabold text-medical-900 text-center mb-5 tracking-tight">Enterprise Security</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-6">
                  {[
                    { icon: Globe2, label: 'UK Sovereign' },
                    { icon: BadgeCheck, label: 'NHS Standards' },
                    { icon: ShieldCheck, label: 'GDPR' },
                    { icon: Lock, label: 'Encrypted' },
                  ].map((b) => (
                    <div key={b.label} className="flex items-center gap-2">
                      <b.icon className="w-6 h-6 text-violet-600 flex-shrink-0" strokeWidth={2} />
                      <span className="text-base font-bold text-medical-800 tracking-tight">{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="py-4 text-center bg-primary-600 text-white flex items-center justify-center min-h-[52px]">
                <p className="text-base font-medium leading-none">Built for UK solicitors and expert witnesses</p>
              </div>
            </div>
          )}
        </div>

        {/* Print-to-PDF helper */}
        <div className="mt-4 text-xs text-medical-500 text-center max-w-md">
          Preview at 500&times;1000px (1:2 ratio). For 1000&times;2000mm print: open browser File &rarr; Print &rarr; "Save as PDF", set paper size to <span className="font-mono">1000mm &times; 2000mm</span> custom, margins 0.
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={!started ? startCountdown : undefined}
      className={`group fixed inset-0 w-screen h-screen overflow-hidden flex flex-col items-center justify-center bg-black ${!started && countdown === null ? 'cursor-pointer' : ''}`}
    >
      {/* Subtle bottom-right marker that reflects what's currently showing. Click toggles between simplified demo and recorded product tour. */}
      {!isRecordMode && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setViewMode(viewMode === 'simplified' ? 'actual' : 'simplified');
          }}
          className="fixed bottom-4 right-4 z-[60] text-xs text-medical-400/80 hover:text-medical-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300 px-1 py-0.5"
        >
          {viewMode === 'simplified' ? '*simplified screens' : '*actual screens'}
        </button>
      )}

      {/* Countdown overlay */}
      {!started && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          {countdown !== null ? (
            <span className="text-7xl font-bold text-primary-600 animate-pulse">{countdown}</span>
          ) : (
            <div className="text-center">
              <p className="text-xl font-semibold text-medical-700 mb-2">Click anywhere to start</p>
              <p className="text-sm text-medical-500">Set up your screen recording first, then click</p>
            </div>
          )}
        </div>
      )}

      {/* Main stage — viewport-filling. 'actual' video uses the whole viewport directly; 'simplified' carousel is authored at 768×580 and CSS-scaled to fit. */}
      {viewMode === 'actual' ? (
        <video
          src="/tour-overview.mp4"
          className="absolute inset-0 w-screen h-screen object-contain bg-black"
          autoPlay
          loop
          muted
          playsInline
        />
      ) : (
      <div
        className="bg-white overflow-hidden flex flex-col justify-between"
        style={{
          width: '1280px',
          height: '720px',
          transform: `scale(${stageScale})`,
          transformOrigin: 'center center',
          flexShrink: 0,
        }}
      >
          {/* Carousel content */}
          <div className="relative" style={{ height: '600px' }}>

            {/* Screen 0: Intro — animated logo assembly */}
            <div className="absolute inset-0 transition-opacity duration-700 flex flex-col items-center justify-center" style={{ opacity: activeScreen === 0 ? 1 : 0, pointerEvents: activeScreen === 0 ? 'auto' : 'none' }}>
              <div className="text-center px-12 max-w-xl mx-auto">
                {/* Animated logo: nodes + lines + text */}
                <div className="mb-8 flex items-center justify-center gap-3">
                  {/* SVG triangle: 3 nodes + 3 connecting lines */}
                  <svg viewBox="0 0 110 100" className="h-[76px] w-auto" style={{ overflow: 'visible' }}>
                    {/* Lines (drawn in step 4). Three segments matching the logo: top-left, top-right, bottom */}
                    {/* Top-left edge: from cyan (bottom-left) to top */}
                    <line x1="22" y1="78" x2="55" y2="20" stroke="#1e3a5f" strokeWidth="9" strokeLinecap="round"
                      style={{
                        strokeDasharray: 70,
                        strokeDashoffset: introStep >= 4 ? 0 : 70,
                        transition: 'stroke-dashoffset 0.5s ease-out',
                      }}
                    />
                    {/* Top-right edge: from top to bottom-right */}
                    <line x1="55" y1="20" x2="88" y2="78" stroke="#1e3a5f" strokeWidth="9" strokeLinecap="round"
                      style={{
                        strokeDasharray: 70,
                        strokeDashoffset: introStep >= 4 ? 0 : 70,
                        transition: 'stroke-dashoffset 0.5s ease-out 0.1s',
                      }}
                    />
                    {/* Bottom edge: from cyan to bottom-right */}
                    <line x1="22" y1="78" x2="88" y2="78" stroke="#1e3a5f" strokeWidth="9" strokeLinecap="round"
                      style={{
                        strokeDasharray: 70,
                        strokeDashoffset: introStep >= 4 ? 0 : 70,
                        transition: 'stroke-dashoffset 0.5s ease-out 0.2s',
                      }}
                    />
                    {/* Cyan node (bottom-left) — appears first */}
                    <circle cx="22" cy="78" r="14" fill="#5dc8d6"
                      style={{
                        opacity: introStep >= 1 ? 1 : 0,
                        transform: introStep >= 1 ? 'scale(1)' : 'scale(0)',
                        transformOrigin: '22px 78px',
                        transition: 'opacity 0.3s ease-out, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }}
                    />
                    {/* Top node — appears second */}
                    <circle cx="55" cy="20" r="14" fill="#1e3a5f"
                      style={{
                        opacity: introStep >= 2 ? 1 : 0,
                        transform: introStep >= 2 ? 'scale(1)' : 'scale(0)',
                        transformOrigin: '55px 20px',
                        transition: 'opacity 0.3s ease-out, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }}
                    />
                    {/* Bottom-right node — appears third */}
                    <circle cx="88" cy="78" r="14" fill="#1e3a5f"
                      style={{
                        opacity: introStep >= 3 ? 1 : 0,
                        transform: introStep >= 3 ? 'scale(1)' : 'scale(0)',
                        transformOrigin: '88px 78px',
                        transition: 'opacity 0.3s ease-out, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }}
                    />
                  </svg>

                  {/* ClinLexis text + tagline (appear after logo assembly) */}
                  <div className="flex flex-col items-start"
                    style={{
                      opacity: introStep >= 5 ? 1 : 0,
                      transform: introStep >= 5 ? 'translateX(0)' : 'translateX(-12px)',
                      transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                    }}
                  >
                    <span className="font-bold text-[68px] leading-none tracking-tight text-medical-900" style={{ fontFamily: "'Nunito Sans', sans-serif" }}>
                      ClinLexis
                      <sup className="text-[17px] font-normal opacity-50 text-medical-400 ml-[-2px] align-super">™</sup>
                    </span>
                    <span
                      className="text-[16px] font-medium uppercase tracking-[0.18em] text-medical-500 -mt-1 pl-1"
                      style={{
                        opacity: introStep >= 6 ? 1 : 0,
                        transition: 'opacity 0.5s ease-out',
                      }}
                    >
                      Read · Analyse · Report
                    </span>
                  </div>
                </div>

                {/* Divider — draws in horizontally */}
                <div className="mx-auto mb-8 overflow-hidden h-0.5" style={{ width: introStep >= 7 ? '64px' : '0px', transition: 'width 0.5s ease-out' }}>
                  <div className="h-full w-16 bg-primary-400/60" />
                </div>

                {/* Headline */}
                <h2 className="text-3xl font-extrabold text-medical-900 leading-snug mb-4 tracking-tight"
                  style={{
                    opacity: introStep >= 8 ? 1 : 0,
                    transform: introStep >= 8 ? 'translateY(0)' : 'translateY(8px)',
                    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
                  }}
                >
                  From thousands of pages<br />
                  <span className="text-primary-600">to a structured, citable case file.</span>
                </h2>

                {/* Subtitle */}
                <p className="text-lg text-medical-500 font-medium mb-5"
                  style={{
                    opacity: introStep >= 8 ? 1 : 0,
                    transform: introStep >= 8 ? 'translateY(0)' : 'translateY(8px)',
                    transition: 'opacity 0.6s ease-out 0.15s, transform 0.6s ease-out 0.15s',
                  }}
                >
                  AI medical record analysis.
                </p>

                {/* UK badge */}
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-medical-200 rounded-full shadow-sm"
                  style={{
                    opacity: introStep >= 8 ? 1 : 0,
                    transform: introStep >= 8 ? 'scale(1)' : 'scale(0.9)',
                    transition: 'opacity 0.6s ease-out 0.3s, transform 0.6s ease-out 0.3s',
                  }}
                >
                  <span className="text-base leading-none">🇬🇧</span>
                  <span className="text-sm font-semibold text-medical-800 tracking-tight">Built for UK legal practice</span>
                </div>
              </div>
            </div>

            {/* Screen 1: Records — document upload */}
            <div className="absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden flex flex-col" style={{ opacity: activeScreen === 1 ? 1 : 0, transform: 'translateX(0)' }}>
              {/* Top nav tabs */}
              <div className={`flex items-center gap-1.5 px-4 py-2 border-b border-medical-100 overflow-x-auto text-[10px] text-medical-500 whitespace-nowrap ${ctaVariant === 'booth' ? 'hidden' : ''}`}>
                {['Records', 'Overview', 'Claimant', 'Chronology', 'Expert Evidence', 'Images', 'Body Map', 'Clinical Standards', 'Evidence Integrity', 'Causation'].map((tab) => (
                  <span key={tab} className={`px-2 py-0.5 ${tab === 'Records' ? 'text-primary-600 font-semibold border-b-2 border-primary-500' : 'text-medical-500'}`}>{tab}</span>
                ))}
              </div>
              <div className="flex-1 px-5 py-4 overflow-hidden">
                {/* Upload area — pulses when files are dropping. Compressed in booth to make room for the processing pipeline. */}
                <div className={`border-2 border-dashed rounded-xl text-center transition-all duration-500 ${
                  ctaVariant === 'booth' ? 'p-3 mb-3' : 'p-5 mb-4'
                } ${
                  uploadPhase === 'dropping' && filesRevealed < 4
                    ? 'border-primary-400 bg-primary-50/50 scale-[1.01]'
                    : 'border-medical-200 bg-medical-50/30'
                }`}>
                  <div className={`mx-auto rounded-full bg-primary-50 flex items-center justify-center transition-transform duration-300 ${
                    ctaVariant === 'booth' ? 'w-8 h-8 mb-1' : 'w-10 h-10 mb-2'
                  } ${
                    uploadPhase === 'dropping' && filesRevealed < 4 ? 'animate-bounce' : ''
                  }`}>
                    <FileText className={`text-primary-500 ${ctaVariant === 'booth' ? 'w-4 h-4' : 'w-5 h-5'}`} />
                  </div>
                  <p className="text-sm font-semibold text-medical-800">
                    {filesRevealed === 0 ? 'Drag and drop medical records here' : filesRevealed < 4 ? 'Dropping files...' : 'All files uploaded'}
                  </p>
                  {ctaVariant !== 'booth' && (
                    <p className="text-xs text-medical-500 mt-1">PDF, DOCX, images, DICOM, ZIP &mdash; max 500MB per file, 1GB per case</p>
                  )}
                </div>
                {/* Uploaded files list — files appear one by one. Booth hides the summary label since it's redundant with the row content + pipeline panel. */}
                <div className={ctaVariant === 'booth' ? 'space-y-1.5' : 'space-y-2'}>
                  {ctaVariant !== 'booth' && (
                    <p className={`text-[10px] font-semibold text-medical-600 uppercase tracking-wide transition-opacity duration-300 ${filesRevealed > 0 ? 'opacity-100' : 'opacity-0'}`}>
                      Uploaded Records &mdash; {Math.min(filesRevealed, 4)} {filesRevealed === 1 ? 'file' : 'files'}{filesRevealed > 0 ? `, ${[312, 599, 755, 847][Math.min(filesRevealed, 4) - 1]} pages` : ''}
                    </p>
                  )}
                  {[
                    { name: 'GP_Records_2010-2024.pdf', pages: 312, type: 'pdf' },
                    { name: 'Royal_Woking_Hospital_Notes.zip', pages: 287, type: 'zip' },
                    { name: 'Neurology_Outpatient_Letters.docx', pages: 156, type: 'docx' },
                    { name: 'Radiology_Reports_1992-2024.pdf', pages: 92, type: 'pdf' },
                  ].map((file, i) => {
                    const Icon = file.type === 'zip' ? FileArchive : FileText;
                    const iconColour = file.type === 'pdf'
                      ? 'text-red-400'
                      : file.type === 'docx'
                        ? 'text-blue-500'
                        : 'text-amber-500';
                    return (
                      <div
                        key={file.name}
                        className="flex items-center gap-3 px-3 py-2 bg-medical-50/70 rounded-lg border border-medical-200 transition-all duration-500"
                        style={{
                          opacity: i < filesRevealed ? 1 : 0,
                          transform: i < filesRevealed ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.95)',
                        }}
                      >
                        <Icon className={`w-4 h-4 ${iconColour} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-medical-800 truncate">{file.name}</p>
                          <p className="text-[10px] text-medical-500">
                            {file.type === 'zip' ? `${file.pages} pages across multiple documents` : `${file.pages} pages`}
                          </p>
                        </div>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded transition-all duration-500 inline-flex items-center gap-1 ${
                          fileStatuses[i] === 'Processed'
                            ? 'bg-green-50 text-green-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}>
                          {i < filesRevealed && fileStatuses[i] === 'Processing' && (
                            <Loader2 className="w-2.5 h-2.5 animate-spin" />
                          )}
                          {i < filesRevealed ? fileStatuses[i] : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {/* Processing summary — booth shows the full pipeline; other variants show a compact progress bar. */}
                {ctaVariant !== 'booth' && (
                <div className={`mt-4 p-3 bg-primary-50/50 rounded-lg border border-primary-100 transition-all duration-500 ${
                  filesRevealed > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-medical-700 font-medium">Total pages processed</span>
                    <span className="text-primary-700 font-bold transition-all duration-300">
                      {uploadPhase === 'done' ? '847' : Math.round(847 * uploadProgress / 100)} / 847
                    </span>
                  </div>
                  <div className="mt-2 h-2 bg-primary-100 rounded-full overflow-hidden">
                    <div className="h-full bg-primary-500 rounded-full transition-all duration-700 ease-out" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="text-[10px] text-medical-500 mt-1.5">
                    {uploadPhase === 'done'
                      ? 'All records processed \u2713'
                      : uploadPhase === 'processing'
                        ? 'OCR complete \u00B7 Extracting clinical events...'
                        : 'Waiting for uploads...'}
                  </p>
                </div>
                )}
                {/* Booth-only: rich processing pipeline showing depth of work. Header dropped — file list label above already shows page count. */}
                {ctaVariant === 'booth' && (
                  <div className={`mt-2 px-3 py-1.5 bg-primary-50/40 rounded-lg border border-primary-100 transition-all duration-500 ${
                    filesRevealed > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                  }`}>
                    <div>
                      {[
                        { icon: Eye, label: 'OCR & handwriting recognition', threshold: 25 },
                        { icon: Layers, label: 'Document classification & deduplication', threshold: 50 },
                        { icon: Clock, label: 'Clinical event & date extraction', threshold: 75 },
                        { icon: Search, label: 'Citation & semantic search indexing', threshold: 100 },
                      ].map((stage, i) => {
                        const prevThreshold = [0, 25, 50, 75][i];
                        const done = uploadProgress >= stage.threshold;
                        const active = !done && uploadProgress >= prevThreshold && filesRevealed > 0;
                        const pending = !done && !active;
                        return (
                          <div key={stage.label} className={`flex items-center gap-2.5 py-0.5 transition-opacity duration-300 ${pending ? 'opacity-40' : 'opacity-100'}`}>
                            <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors duration-500 ${
                              done ? 'bg-emerald-50' : active ? 'bg-primary-100' : 'bg-medical-100'
                            }`}>
                              <stage.icon className={`w-3 h-3 transition-colors duration-500 ${
                                done ? 'text-emerald-600' : active ? 'text-primary-600' : 'text-medical-400'
                              }`} />
                            </div>
                            <span className="flex-1 text-xs font-medium text-medical-800 leading-tight">{stage.label}</span>
                            {done ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" strokeWidth={3} />
                            ) : active ? (
                              <Loader2 className="w-3 h-3 text-primary-500 animate-spin flex-shrink-0" />
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Screen 2: Overview — case summary */}
            <div className="absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden flex flex-col" style={{ opacity: activeScreen === 2 ? 1 : 0, transform: 'translateX(0)' }}>
              <div className={`flex items-center gap-1.5 px-4 py-2 border-b border-medical-100 overflow-x-auto text-[10px] text-medical-500 whitespace-nowrap ${ctaVariant === 'booth' ? 'hidden' : ''}`}>
                {['Records', 'Overview', 'Claimant', 'Chronology', 'Expert Evidence', 'Images', 'Body Map', 'Clinical Standards', 'Evidence Integrity', 'Causation'].map((tab) => (
                  <span key={tab} className={`px-2 py-0.5 ${tab === 'Overview' ? 'text-primary-600 font-semibold border-b-2 border-primary-500' : 'text-medical-500'}`}>{tab}</span>
                ))}
              </div>
              <div className="flex-1 overflow-hidden relative">
                <div
                  ref={overviewContentRef}
                  className="absolute left-0 right-0 px-5 py-4 space-y-2.5"
                  style={{
                    transform: `translateY(${-(overviewScroll / 100) * overviewMaxScroll}px)`,
                    transition: 'transform 0.05s linear',
                  }}
                >
                  <div className="p-3 rounded-lg border border-medical-200 bg-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-primary-500" />
                      <p className="text-xs font-bold text-medical-900">Key Finding</p>
                    </div>
                    <p className="text-[10px] text-medical-700 leading-relaxed">Jane Doe is a 59-year-old female (DOB 01/05/1965)<sup className="text-amber-500 text-[8px]">*</sup> with secondary progressive multiple sclerosis since 1992<sup className="text-amber-500 text-[8px]">*</sup>, in ill-health retirement from nursing since 2010<sup className="text-amber-500 text-[8px]">*</sup>. Pre-collision baseline mobility was approximately 50 metres with a walking frame<sup className="text-amber-500 text-[8px]">*</sup>. On 11 November 2024 she was involved in a rear-end RTC on the A247 near Send, Surrey, sustaining an acute left frontoparietal subdural haematoma, right parietal linear skull fracture, C7&ndash;T2 spinous process fractures, and severe right hip abductor mechanism injury<sup className="text-amber-500 text-[8px]">*</sup>. Admitted to Royal Woking Hospital for 16 days; discharged 27 November 2024<sup className="text-amber-500 text-[8px]">*</sup>. As of March 2026, partial but incomplete recovery to her pre-collision baseline, with residual right hip pain and ongoing psychological sequelae<sup className="text-amber-500 text-[8px]">*</sup>.</p>
                  </div>

                  <div className="p-3 rounded-lg border border-red-200 bg-gradient-to-br from-red-50 to-orange-50">
                    <div className="flex items-center gap-2 mb-1.5">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <p className="text-xs font-bold text-medical-900">The Index Event</p>
                    </div>
                    <p className="text-[10px] text-medical-700 leading-relaxed">On 11 November 2024 at 15:02, the claimant was driving a stationary vehicle on the A247 near Send, Surrey, when struck from behind at approximately 35 mph<sup className="text-amber-500 text-[8px]">*</sup>. Seatbelted; airbags did not deploy. A brief loss of consciousness of 3&ndash;4 minutes was witnessed<sup className="text-amber-500 text-[8px]">*</sup>. CT head: acute left frontoparietal subdural haematoma (4 mm) with trace falx extension; right parietal linear skull fracture<sup className="text-amber-500 text-[8px]">*</sup>. CT spine: non-displaced C7, T1, T2 spinous process fractures<sup className="text-amber-500 text-[8px]">*</sup>. MRI right hip: post-traumatic greater trochanteric pain syndrome with partial-thickness gluteus medius/minimus tears<sup className="text-amber-500 text-[8px]">*</sup>. GCS 15 on arrival.</p>
                  </div>

                  <div className="p-3 rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-violet-50">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Zap className="w-4 h-4 text-purple-600" />
                      <p className="text-xs font-bold text-medical-900">Treatment Course</p>
                    </div>
                    <p className="text-[10px] text-medical-700 leading-relaxed">Cervical collar applied at scene; conveyed under trauma pre-alert to Royal Woking Hospital<sup className="text-amber-500 text-[8px]">*</sup>. Repeat CT on 12 November confirmed a stable subdural haematoma; neurosurgery recommended conservative management<sup className="text-amber-500 text-[8px]">*</sup>. 16-day admission with multidisciplinary input from neurosurgery, orthopaedics, physiotherapy and OT<sup className="text-amber-500 text-[8px]">*</sup>. Mobility progressed from inability to stand on 12 November, to ~5 metres with frame by 25 November<sup className="text-amber-500 text-[8px]">*</sup>. Discharged 27 November 2024<sup className="text-amber-500 text-[8px]">*</sup>. Community physiotherapy from 8 January 2025; orthopaedic follow-up; sertraline increased to 150 mg for emerging post-traumatic anxiety<sup className="text-amber-500 text-[8px]">*</sup>.</p>
                  </div>

                  <div className="p-3 rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50">
                    <div className="flex items-center gap-2 mb-1.5">
                      <BarChart3 className="w-4 h-4 text-amber-600" />
                      <p className="text-xs font-bold text-medical-900">Current Status &amp; Impact</p>
                    </div>
                    <p className="text-[10px] text-medical-700 leading-relaxed">As of March 2026, mobilises 40&ndash;50 metres with a frame on good days &mdash; partial but incomplete recovery to pre-collision baseline<sup className="text-amber-500 text-[8px]">*</sup>. Residual right hip pain on prolonged standing<sup className="text-amber-500 text-[8px]">*</sup>; stairs remain a major obstacle, bedroom relocated to ground floor<sup className="text-amber-500 text-[8px]">*</sup>. EDSS estimated at 6.5&ndash;7.0<sup className="text-amber-500 text-[8px]">*</sup>. Requires daily assistance from her daughter for shopping and heavier domestic tasks<sup className="text-amber-500 text-[8px]">*</sup>. Post-traumatic stress symptoms emerged following the collision &mdash; intrusive recollections, hypervigilance, avoidance of busier roads<sup className="text-amber-500 text-[8px]">*</sup>. Mood improved by early 2026 but vulnerable to relapse if sertraline reduced<sup className="text-amber-500 text-[8px]">*</sup>.</p>
                  </div>

                  <div className="p-3 rounded-lg border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                    <div className="flex items-center gap-2 mb-1.5">
                      <TrendingDown className="w-4 h-4 text-green-700 rotate-180" />
                      <p className="text-xs font-bold text-medical-900">Prognosis &amp; Outlook</p>
                    </div>
                    <p className="text-[10px] text-medical-700 leading-relaxed">Underlying secondary progressive MS characterised as biologically stable, with gradual disease progression rather than inflammatory relapse<sup className="text-amber-500 text-[8px]">*</sup>. Treating neurologist's view: the collision has layered pain, deconditioning, loss of confidence and psychological distress onto an already progressive condition; the reduction in mobility since November 2024 is not explained by new inflammatory disease<sup className="text-amber-500 text-[8px]">*</sup>. Physiotherapy prognosis: guarded but positive for gradual gains<sup className="text-amber-500 text-[8px]">*</sup>. Documented that full return to pre-accident baseline cannot be guaranteed due to the interplay of post-traumatic injury with pre-existing progressive MS<sup className="text-amber-500 text-[8px]">*</sup>.</p>
                  </div>
                </div>

                {/* Subtle scroll indicator */}
                <div className="absolute right-1 top-2 bottom-2 w-1 bg-medical-200/40 rounded-full">
                  <div
                    className="w-full bg-primary-500/60 rounded-full"
                    style={{ height: '20%', transform: `translateY(${overviewScroll * 4}px)`, transition: 'transform 0.05s linear' }}
                  />
                </div>
              </div>
            </div>


            {/* Screen 3: Chronology + AI filter (citation flow) */}
            <div className="absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden flex flex-col" style={{ opacity: activeScreen === 3 ? 1 : 0, transform: 'translateX(0)' }}>
              <div className={`flex items-center gap-1.5 px-4 py-2 border-b border-medical-100 overflow-x-auto text-[10px] text-medical-500 whitespace-nowrap ${ctaVariant === 'booth' ? 'hidden' : ''}`}>
                {['Records', 'Overview', 'Claimant', 'Chronology', 'Expert Evidence', 'Images', 'Body Map', 'Clinical Standards', 'Evidence Integrity', 'Causation'].map((tab) => (
                  <span key={tab} className={`px-2 py-0.5 ${tab === 'Chronology' ? 'text-primary-600 font-semibold border-b-2 border-primary-500' : 'text-medical-500'}`}>{tab}</span>
                ))}
              </div>
              {/* Search bar with typing animation */}
              <div className="mx-4 mt-2.5 mb-2">
                <div className="flex items-center bg-white border border-medical-200 rounded-lg px-3 py-2">
                  <Search className="h-4 w-4 text-medical-400 mr-2 flex-shrink-0" />
                  {filterPhase === 'idle' ? (
                    <span className="text-medical-400 text-xs">97 of 101 events &mdash; ask anything, e.g. &ldquo;show only MS relapse events&rdquo;</span>
                  ) : (
                    <>
                      <span className="text-medical-800 text-xs">{filterQuery.slice(0, typedChars)}</span>
                      {filterPhase === 'typing' && <span className="w-0.5 h-4 bg-primary-500 animate-pulse ml-0.5" />}
                    </>
                  )}
                  <span className={`ml-auto px-2.5 py-1 rounded text-[10px] font-medium ${filterPhase === 'filtered' || filterPhase === 'citation' ? 'bg-primary-600 text-white' : 'bg-medical-100 text-medical-500'}`}>Filter</span>
                </div>
              </div>
              {/* Filter bar */}
              <div className="flex items-center gap-2.5 px-4 mb-2 text-[10px]">
                <span className="font-semibold text-primary-600 flex items-center gap-1">&#x25A3; Narrative</span>
                <span className="text-medical-500 flex items-center gap-1">&#x2581; All Events</span>
                <span className="text-medical-200">|</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Key 22</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Supporting 75</span>
                <span className="flex items-center gap-1 text-medical-400 line-through"><span className="w-2 h-2 rounded-full bg-medical-300" />Routine 4</span>
                <span className="flex items-center gap-1 text-medical-400 line-through"><span className="w-2 h-2 rounded-full bg-medical-200" />Sub-events</span>
                <span className="text-medical-200">|</span>
                <span className="text-[10px] text-medical-500 flex items-center gap-1">&#x21C5; Newest</span>
                <span className="text-[10px] text-medical-400">Oldest</span>
              </div>

              {/* Content area */}
              <div className="flex-1 overflow-hidden relative">
                {/* Full chronology — stays visible across idle, typing, and citation phases.
                    During citation phases the right edge narrows so the source document panel can slide in.
                    Inner content can scroll vertically (chronoScrollY) to bring later events into focus before the second citation click. */}
                <div
                  className="absolute top-0 bottom-0 left-0 px-4 transition-all duration-700 overflow-hidden"
                  style={{
                    opacity: (filterPhase === 'idle' || filterPhase === 'typing' || filterPhase === 'citation-1' || filterPhase === 'citation-2') ? 1 : 0,
                    right: (filterPhase === 'citation-1' || filterPhase === 'citation-2') ? '44%' : '0%',
                  }}
                >
                <div
                  style={{
                    transform: `translateY(${chronoScrollY}px)`,
                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <div className="p-3 bg-amber-50/70 rounded-lg border border-amber-200/60 mb-2.5">
                    <p className="text-[10px] font-bold text-amber-600 tracking-wide mb-1">AT A GLANCE</p>
                    <p className="text-[10px] text-medical-700 leading-relaxed">The claimant has a longstanding history of multiple sclerosis, diagnosed in November 1992 and secondary progressive by 2009. On 11 November 2024 she was involved in a rear-end RTC on the A247 near Send, Surrey, sustaining a left frontoparietal subdural haematoma, right parietal skull fracture, and severe right hip abductor injury. Admitted to Royal Woking Hospital for 16 days; discharged 27 November 2024 with ongoing physiotherapy, orthopaedic and psychological follow-up.</p>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      {
                        title: '1992–2024 — MS background and pre-collision history',
                        events: 25,
                        summary: 'MS first diagnosed by Dr Lawson in November 1992 with periventricular lesions on MRI. Brainstem relapse with VI palsy in June 1996, treated with IV methylprednisolone. Transition to secondary progressive confirmed September 2009. Ill-health retirement from nursing in 2010.',
                        rawEvents: [
                          { date: '17/11/1992', category: 'Diagnosis', dotColour: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700', title: 'Clinically Definite MS Confirmed (Dr Lawson)', body: <>Right optic neuritis followed by sensory myelitis; <span className="border-b border-dotted border-amber-400">periventricular lesions on MRI</span><sup className="text-amber-500 text-[7px]">*</sup> with positive VEPs and oligoclonal bands.</> },
                          { date: '22/06/1996', category: 'Relapse', dotColour: 'bg-red-500', badge: 'bg-red-50 text-red-700', title: 'Brainstem Relapse — Diplopia and VI Nerve Palsy', body: <>Acute brainstem relapse with right <span className="border-b border-dotted border-amber-400">VI nerve palsy</span><sup className="text-amber-500 text-[7px]">*</sup>. Treated with 2 g IV methylprednisolone; diplopia significantly improved.</> },
                          { date: '09/2009', category: 'Status change', dotColour: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700', title: 'Transition to Secondary Progressive MS', body: <>Gradual accrual of disability without discrete relapse over preceding 18 months. <span className="border-b border-dotted border-amber-400">EDSS 4.5</span><sup className="text-amber-500 text-[7px]">*</sup>; bladder urgency documented.</> },
                          { date: '10/2024', category: 'Baseline', dotColour: 'bg-violet-500', badge: 'bg-violet-50 text-violet-700', title: 'Final Pre-Collision Neurology Review', body: <><span className="border-b border-dotted border-amber-400">50 m walking with frame</span><sup className="text-amber-500 text-[7px]">*</sup>; scooter for community distances. EDSS 6.5. Independent transfers, lives alone with daughter nearby.</> },
                        ] as Array<{ date: string; category: string; dotColour: string; badge: string; title: string; body: React.ReactNode }>,
                      },
                      {
                        title: '11 November 2024 — The index collision and emergency response',
                        events: 5,
                        summary: 'Rear-end RTC at 15:02 on the A247 near Send, Surrey. Vehicle struck from behind at approx 35 mph; brief LOC of 3–4 minutes. CT confirmed acute left frontoparietal subdural haematoma and right parietal skull fracture.',
                        rawEvents: [],
                      },
                      {
                        title: '12–27 November 2024 — Inpatient admission and discharge',
                        events: 10,
                        summary: 'Conservative management of stable subdural haematoma. Multidisciplinary rehabilitation with neurosurgery, orthopaedics, physiotherapy and OT input. Discharged 27 November 2024 with community follow-up.',
                        rawEvents: [],
                      },
                      {
                        title: '28 November 2024 – March 2026 — Post-discharge recovery and follow-up',
                        events: 57,
                        summary: 'Community physiotherapy from 8 January 2025; orthopaedic follow-up to 22 May 2025 (35–40 m walking with frame). Sertraline increased for emerging post-traumatic anxiety; trauma-focused CBT referral. Final neurology review October 2025: EDSS 6.5–7.0; partial but incomplete recovery to pre-collision baseline.',
                        rawEvents: [],
                      },
                    ].map((period, i) => {
                      const isExpanded = expandedPeriod === i;
                      const isRippling = periodClickRipple === i;
                      return (
                        <div
                          key={period.title}
                          className={`rounded-lg transition-all duration-300 ${
                            isExpanded
                              ? 'bg-primary-50/40 border border-primary-200 px-2.5 py-2'
                              : isRippling
                                ? 'bg-primary-50/70 ring-2 ring-primary-300 px-2 py-1.5'
                                : 'border border-transparent px-2 py-1.5'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-medical-900 flex items-center gap-1">
                              <span className={`text-medical-400 text-[10px] inline-block transition-transform duration-300 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>&#9662;</span> {period.title}
                            </p>
                            <span className="text-[10px] text-medical-400 whitespace-nowrap">{period.events} events</span>
                          </div>
                          <p className="text-[10px] text-medical-600 leading-relaxed mt-1 ml-3">{period.summary}</p>
                          {/* Raw events — fan out on click. CSS grid trick lets us animate height without measuring. */}
                          <div
                            className="grid transition-all duration-500 ease-out ml-3"
                            style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr', opacity: isExpanded ? 1 : 0 }}
                          >
                            <div className="overflow-hidden">
                              <div className="mt-2 pt-2 border-t border-primary-200/50 space-y-2">
                                {period.rawEvents.map((ev, j) => {
                                  const isRippling = i === 0 && eventClickRipple === j;
                                  const isSelected = i === 0 && (
                                    (filterPhase === 'citation-1' && j === 0) ||
                                    (filterPhase === 'citation-2' && j === 1)
                                  );
                                  return (
                                  <div
                                    key={`${i}-${j}`}
                                    className={`flex items-start gap-2 rounded-md transition-all duration-300 ${
                                      isSelected
                                        ? 'bg-primary-50/80 ring-1 ring-primary-300 px-1.5 py-0.5 -mx-1.5'
                                        : isRippling
                                          ? 'bg-primary-100/60 ring-2 ring-primary-300 px-1.5 py-0.5 -mx-1.5'
                                          : ''
                                    }`}
                                    style={{
                                      opacity: isExpanded ? 1 : 0,
                                      transform: isExpanded ? 'translateX(0)' : 'translateX(-8px)',
                                      transitionDelay: isExpanded ? `${100 + j * 80}ms` : '0ms',
                                    }}
                                  >
                                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${ev.dotColour}`} />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-1.5 mb-0.5">
                                        <span className={`px-1.5 py-0 rounded text-[9px] font-medium ${ev.badge}`}>{ev.category}</span>
                                        <span className="text-[9px] text-medical-500">{ev.date}</span>
                                      </div>
                                      <p className="text-[10px] font-semibold text-medical-900 leading-tight">{ev.title}</p>
                                      <p className="text-[9.5px] text-medical-600 leading-snug mt-0.5">{ev.body}</p>
                                    </div>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                </div>

                {/* Filtered results */}
                <div className="absolute inset-0 px-4 transition-all duration-500 overflow-hidden" style={{ opacity: filterPhase === 'filtered' ? 1 : 0 }}>
                  <div className="p-2.5 bg-medical-50 rounded-lg border border-medical-200/50 mb-3">
                    <p className="text-[10px] font-medium text-medical-700 flex items-center gap-1">
                      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" /></svg>
                      Filtered &mdash; showing 4 of 108 events
                    </p>
                    <p className="text-[9px] text-medical-500 mt-0.5">MS relapses with IV steroid treatment, 1992&ndash;2000</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-[10px] font-medium">Diagnosis</span>
                          <span className="text-[10px] text-medical-500">17/11/1992</span>
                        </div>
                        <p className="font-medium text-xs text-medical-900">Clinically Definite MS Confirmed (Dr Lawson)</p>
                        <p className="text-[10px] text-medical-700 mt-0.5 leading-relaxed">
                          Clinically definite multiple sclerosis confirmed by <span className="border-b border-dotted border-amber-400">Dr Lawson</span><sup className="text-amber-500 text-[8px]">*</sup> following right optic neuritis and sensory myelitis. Periventricular lesions on <span className="border-b border-dotted border-amber-400">MRI</span><sup className="text-amber-500 text-[8px]">*</sup> with positive VEP and oligoclonal bands.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-[10px] font-medium">Relapse</span>
                          <span className="text-[10px] text-medical-500">22/06/1996</span>
                        </div>
                        <p className="font-medium text-xs text-medical-900">Brainstem Relapse &mdash; Diplopia and VI Nerve Palsy</p>
                        <p className="text-[10px] text-medical-700 mt-0.5 leading-relaxed">
                          Acute brainstem relapse with diplopia and right <span className="border-b border-dotted border-amber-400">VI (abducens) nerve palsy</span><sup className="text-amber-500 text-[8px]">*</sup>. Admitted under Dr Lawson for IV methylprednisolone.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded text-[10px] font-medium">Treatment</span>
                          <span className="text-[10px] text-medical-500">25/06/1996</span>
                        </div>
                        <p className="font-medium text-xs text-medical-900">IV Methylprednisolone Course Completed &mdash; Discharge</p>
                        <p className="text-[10px] text-medical-600">Dr Lawson &mdash; Royal Woking Hospital, Neurology</p>
                        <p className="text-[10px] text-medical-700 mt-0.5 leading-relaxed">
                          Completed 2 days of 1g IV methylprednisolone. <span className="border-b border-dotted border-amber-400">Diplopia significantly improved</span><sup className="text-amber-500 text-[8px]">*</sup>; right VI palsy resolving. Medically fit for discharge.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-[10px] font-medium">Relapse</span>
                          <span className="text-[10px] text-medical-500">15/03/2000</span>
                        </div>
                        <p className="font-medium text-xs text-medical-900">Relapse &mdash; Left Leg Weakness, IV Steroids</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Citation view */}
                <div className="absolute inset-0 transition-all duration-700 overflow-hidden flex" style={{ opacity: filterPhase === 'citation' ? 1 : 0 }}>
                  {/* Left: filtered events with one selected */}
                  <div className="flex-1 overflow-hidden p-3">
                    <div className="flex items-center gap-2.5 mb-2 text-[10px]">
                      <span className="font-semibold text-primary-600">Narrative</span>
                      <span className="text-medical-500">All Events</span>
                      <span className="text-medical-200">|</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Key 22</span>
                    </div>
                    <div className="p-2 bg-medical-50 rounded-lg border border-medical-200/50 mb-2">
                      <p className="text-[9px] font-medium text-medical-700 flex items-center gap-1">
                        <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" /></svg>
                        Filtered &mdash; showing 4 of 108 events
                      </p>
                    </div>
                    <div className="space-y-2">
                      {/* Event 1 */}
                      <div className="flex items-start gap-2 px-2">
                        <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                        <div>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[9px] font-medium">Diagnosis</span>
                            <span className="text-[9px] text-medical-500">17/11/1992</span>
                          </div>
                          <p className="font-medium text-[10px] text-medical-900">Clinically Definite MS Confirmed (Dr Lawson)</p>
                        </div>
                      </div>
                      {/* Event 2 */}
                      <div className="flex items-start gap-2 px-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                        <div>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="px-1.5 py-0.5 bg-red-50 text-red-700 rounded text-[9px] font-medium">Relapse</span>
                            <span className="text-[9px] text-medical-500">22/06/1996</span>
                          </div>
                          <p className="font-medium text-[10px] text-medical-900">Brainstem Relapse &mdash; Diplopia and VI Palsy</p>
                        </div>
                      </div>
                      {/* Event 3 — SELECTED (the cited one) */}
                      <div className="flex items-start gap-2 p-2 rounded-lg bg-primary-50/60 border border-primary-200 ring-1 ring-primary-300">
                        <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                        <div>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="px-1.5 py-0.5 bg-cyan-50 text-cyan-700 rounded text-[9px] font-medium">Treatment</span>
                            <span className="text-[9px] text-medical-500">25/06/1996</span>
                          </div>
                          <p className="font-medium text-[10px] text-medical-900">IV Methylprednisolone Course Completed &mdash; Discharge</p>
                          <p className="text-[9px] text-medical-600">Dr Lawson &mdash; Royal Woking Hospital, Neurology</p>
                          <p className="text-[9px] text-medical-700 mt-0.5 leading-relaxed">
                            Completed 2 days of 1g IV methylprednisolone. <span className="border-b-2 border-amber-400 bg-amber-50 cursor-pointer">Diplopia significantly improved</span><sup className="text-amber-500 text-[7px]">*</sup>; VI palsy resolving. Medically fit for discharge.
                          </p>
                        </div>
                      </div>
                      {/* Event 4 */}
                      <div className="flex items-start gap-2 px-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                        <div>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="px-1.5 py-0.5 bg-red-50 text-red-700 rounded text-[9px] font-medium">Relapse</span>
                            <span className="text-[9px] text-medical-500">15/03/2000</span>
                          </div>
                          <p className="font-medium text-[10px] text-medical-900">Relapse &mdash; Left Leg Weakness</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Source document sidebar — handwritten note */}
                  <div className="bg-medical-50/30 flex flex-col overflow-hidden transition-all duration-700" style={{ width: filterPhase === 'citation' ? '44%' : '0%', opacity: filterPhase === 'citation' ? 1 : 0 }}>
                    {/* Compact single header strip */}
                    <div className="px-3 py-1.5 bg-white border-b border-medical-100 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <FileText className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold text-medical-900 whitespace-nowrap truncate">Neurology Inpatient Notes</p>
                          <p className="text-[8px] text-medical-500 whitespace-nowrap truncate">Dr Lawson &middot; Royal Woking &middot; 25/06/1996</p>
                        </div>
                      </div>
                      <span className="text-medical-400 text-sm cursor-pointer flex-shrink-0">&times;</span>
                    </div>
                    <div className="flex-1 overflow-hidden bg-[#e9eef0] p-1 flex items-center justify-center">
                      {/* Handwritten note — actual scanned image */}
                      <img
                        src="/images/clinical-note-1996.png"
                        alt="Dr Lawson clinical note, 25 June 1996"
                        className="max-w-full max-h-full object-contain bg-white shadow-md"
                      />
                    </div>
                  </div>
                </div>

                {/* Source document panel — slides in from the right during citation phases. Chronology view stays exactly as-is, just narrowed. */}
                <div
                  className="absolute top-0 bottom-0 right-0 transition-all duration-700 overflow-hidden bg-medical-50/30 flex flex-col flex-shrink-0"
                  style={{
                    width: (filterPhase === 'citation-1' || filterPhase === 'citation-2') ? '44%' : '0%',
                    opacity: (filterPhase === 'citation-1' || filterPhase === 'citation-2') ? 1 : 0,
                  }}
                >
                  <div className="px-3 py-1.5 bg-white border-b border-medical-100 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <FileText className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold text-medical-900 whitespace-nowrap truncate">
                          {filterPhase === 'citation-2' ? 'Neurology Inpatient Notes' : 'Neurology Outpatient Letter'}
                        </p>
                        <p className="text-[8px] text-medical-500 whitespace-nowrap truncate">
                          {filterPhase === 'citation-2' ? 'Dr Lawson · Royal Woking · 25/06/1996' : 'Dr Lawson · Royal Woking · 17/11/1992'}
                        </p>
                      </div>
                    </div>
                    <span className="text-medical-400 text-sm flex-shrink-0">×</span>
                  </div>

                  <div className="flex-1 overflow-hidden bg-[#e9eef0] p-2 flex items-center justify-center">
                    {filterPhase === 'citation-2' ? (
                      // Handwritten admission note. The page is photographed against cream lined paper, so the highlighter
                      // is anchored using the image's known 908×1116 aspect ratio (no overlap with paper colour).
                      <div className="relative bg-white shadow-md" style={{ aspectRatio: '908 / 1116', maxWidth: '100%', maxHeight: '100%', height: '100%' }}>
                        <img
                          src="/images/clinical-note-1996.png"
                          alt="Dr Lawson clinical note, 25 June 1996"
                          className="absolute inset-0 w-full h-full"
                        />
                        {/* Yellow highlighter on "Diplopia significantly improved" — solid yellow, no blend mode (multiply on cream paper read as double-yellow) */}
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: '14%',
                            top: '47%',
                            width: '50%',
                            height: '4.2%',
                            background: 'rgba(255, 235, 59, 0.55)',
                          }}
                        />
                      </div>
                    ) : (
                      // Typed neurology consultant letter, key diagnostic sentence highlighted
                      <div className="bg-white shadow-md max-w-full max-h-full overflow-hidden p-3" style={{ fontFamily: 'Georgia, "Times New Roman", serif', aspectRatio: '5 / 6.5' }}>
                        <div className="text-center pb-1 mb-1.5 border-b border-medical-300">
                          <p className="text-[6.5px] font-bold tracking-[0.15em] text-medical-900">DEPARTMENT OF NEUROLOGY</p>
                          <p className="text-[5.5px] text-medical-600 mt-0.5">Royal Woking Hospital · Heathside Road · Woking · GU22 7HS</p>
                        </div>
                        <p className="text-[6.5px] text-medical-800 mb-1">17 November 1992</p>
                        <p className="text-[6.5px] text-medical-800 mb-1.5"><strong>Re:</strong> Mrs Jane Doe, DOB 01/05/1965</p>
                        <p className="text-[6.5px] text-medical-800 mb-1">Dear Dr Edwards,</p>
                        <p className="text-[6.5px] text-medical-800 leading-snug mb-1.5">
                          Thank you for referring this 27-year-old woman, whom I had the pleasure of seeing in clinic today.
                        </p>
                        <p className="text-[6.5px] text-medical-800 leading-snug mb-1.5">
                          She gives a history of right optic neuritis in 1991 with partial recovery, followed three months ago by ascending sensory disturbance consistent with a partial myelitis.
                        </p>
                        <p className="text-[6.5px] text-medical-800 leading-snug mb-1.5">
                          MRI of the brain demonstrates multiple ovoid periventricular lesions oriented perpendicular to the lateral ventricles. Visual evoked responses are delayed on the right. CSF examination shows oligoclonal bands not present in serum.
                        </p>
                        <p className="text-[6.5px] text-medical-800 leading-snug mb-1.5">
                          In summary, <span className="bg-yellow-300/80 px-0.5 rounded-sm">the clinical history, examination findings, MRI appearances, delayed VEPs and oligoclonal bands together support a diagnosis of clinically definite multiple sclerosis</span>.
                        </p>
                        <p className="text-[6.5px] text-medical-800 leading-snug mb-2">
                          I have explained this to Mrs Doe and arranged 6-week follow-up.
                        </p>
                        <p className="text-[6.5px] text-medical-700">Yours sincerely,</p>
                        <p className="text-[6.5px] font-semibold text-medical-900 mt-1">Dr A Lawson</p>
                        <p className="text-[5.5px] text-medical-600">Consultant Neurologist</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Screen 4: Report drafting */}
            <div className="absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden flex flex-col" style={{ opacity: activeScreen === 4 ? 1 : 0, transform: 'translateX(0)' }}>
              <div className={`flex items-center gap-1.5 px-4 py-2 border-b border-medical-100 overflow-x-auto text-[10px] text-medical-500 whitespace-nowrap ${ctaVariant === 'booth' ? 'hidden' : ''}`}>
                {['Records', 'Overview', 'Claimant', 'Chronology', 'Expert Evidence', 'Images', 'Body Map', 'Clinical Standards', 'Evidence Integrity', 'Causation'].map((tab) => (
                  <span key={tab} className="px-2 py-0.5 text-medical-500">{tab}</span>
                ))}
              </div>
              <div className="flex-1 px-5 py-4 overflow-hidden relative">
                {/* Options view */}
                <div className={`absolute inset-0 transition-all duration-500 ${ctaVariant === 'booth' ? 'px-5 py-3' : 'px-5 py-4'}`} style={{ opacity: reportPhase === 'drafting' ? 0 : 1 }}>
                  <div className={`flex items-center gap-2 ${ctaVariant === 'booth' ? 'mb-4' : 'mb-4'}`}>
                    <Zap className={`text-primary-500 ${ctaVariant === 'booth' ? 'w-6 h-6' : 'w-4 h-4'}`} />
                    <p className={`font-bold text-medical-900 ${ctaVariant === 'booth' ? 'text-2xl' : 'text-sm'}`}>Draft a new report</p>
                  </div>
                  <div className={ctaVariant === 'booth' ? 'space-y-2' : 'space-y-2.5'}>
                    {/* Key Findings */}
                    <div className={`rounded-xl border border-medical-200 flex items-center cursor-pointer ${
                      ctaVariant === 'booth'
                        ? 'p-4 gap-4 bg-gradient-to-br from-white via-white to-amber-50/70'
                        : 'p-3.5 gap-3 bg-white'
                    }`}>
                      <div className={`rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 ${ctaVariant === 'booth' ? 'w-12 h-12' : 'w-10 h-10'}`}>
                        <Star className={`text-amber-500 ${ctaVariant === 'booth' ? 'w-6 h-6' : 'w-5 h-5'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-medical-900 leading-tight ${ctaVariant === 'booth' ? 'text-xl mb-0.5' : 'text-xs'}`}>Key Findings Report</p>
                        <p className={`text-medical-500 leading-snug ${ctaVariant === 'booth' ? 'text-sm' : 'text-[10px]'}`}>Synthesise the starred findings into a structured report</p>
                      </div>
                      <ChevronRight className={`text-medical-300 flex-shrink-0 ${ctaVariant === 'booth' ? 'w-6 h-6' : 'w-5 h-5'}`} />
                    </div>

                    {/* Chronology Summary — selectable / selected */}
                    <div className={`rounded-xl flex items-center cursor-pointer transition-all duration-500 ${
                      ctaVariant === 'booth' ? 'p-4 gap-4' : 'p-3.5 gap-3'
                    } ${
                      reportPhase === 'selected'
                        ? `border-2 border-primary-400 ring-2 ring-primary-200 scale-[1.02] ${ctaVariant === 'booth' ? 'bg-gradient-to-br from-primary-50 via-primary-50 to-primary-100/70' : 'bg-primary-50'}`
                        : `border border-primary-200 ${ctaVariant === 'booth' ? 'bg-gradient-to-br from-white via-white to-primary-50/70' : 'bg-primary-50/30'}`
                    }`}>
                      <div className={`rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 ${ctaVariant === 'booth' ? 'w-12 h-12' : 'w-10 h-10'}`}>
                        <Clock className={`text-primary-600 ${ctaVariant === 'booth' ? 'w-6 h-6' : 'w-5 h-5'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-medical-900 leading-tight ${ctaVariant === 'booth' ? 'text-xl mb-0.5' : 'text-xs'}`}>Chronology Summary</p>
                        <p className={`text-primary-600 leading-snug ${ctaVariant === 'booth' ? 'text-sm' : 'text-[10px]'}`}>Structured chronological narrative of the medical records</p>
                      </div>
                      <ChevronRight className={`text-primary-300 flex-shrink-0 ${ctaVariant === 'booth' ? 'w-6 h-6' : 'w-5 h-5'}`} />
                    </div>

                    {/* Mirror a previous report */}
                    <div className={`rounded-xl border border-dashed border-medical-200 flex items-center cursor-pointer ${
                      ctaVariant === 'booth'
                        ? 'p-4 gap-4 bg-gradient-to-br from-white via-white to-medical-50/70'
                        : 'p-3.5 gap-3 bg-white'
                    }`}>
                      <div className={`rounded-lg bg-medical-100 flex items-center justify-center flex-shrink-0 ${ctaVariant === 'booth' ? 'w-12 h-12' : 'w-10 h-10'}`}>
                        <Upload className={`text-medical-500 ${ctaVariant === 'booth' ? 'w-6 h-6' : 'w-5 h-5'}`} strokeWidth={2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-medical-900 leading-tight ${ctaVariant === 'booth' ? 'text-xl mb-0.5' : 'text-xs'}`}>Mirror a previous report</p>
                        <p className={`text-medical-500 leading-snug ${ctaVariant === 'booth' ? 'text-sm' : 'text-[10px]'}`}>Upload a Word report to create a reusable template</p>
                      </div>
                      <ChevronRight className={`text-medical-300 flex-shrink-0 ${ctaVariant === 'booth' ? 'w-6 h-6' : 'w-5 h-5'}`} />
                    </div>

                    {/* Use a template */}
                    <div className={`rounded-xl border border-dashed border-medical-200 flex items-center cursor-pointer ${
                      ctaVariant === 'booth'
                        ? 'p-4 gap-4 bg-gradient-to-br from-white via-white to-medical-50/70'
                        : 'p-3.5 gap-3 bg-white'
                    }`}>
                      <div className={`rounded-lg bg-medical-100 flex items-center justify-center flex-shrink-0 ${ctaVariant === 'booth' ? 'w-12 h-12' : 'w-10 h-10'}`}>
                        <FileText className={`text-medical-500 ${ctaVariant === 'booth' ? 'w-6 h-6' : 'w-5 h-5'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-medical-900 leading-tight ${ctaVariant === 'booth' ? 'text-xl mb-0.5' : 'text-xs'}`}>Use a template</p>
                        <p className={`text-medical-500 leading-snug ${ctaVariant === 'booth' ? 'text-sm' : 'text-[10px]'}`}>Browse specialism-specific report templates</p>
                      </div>
                      <ChevronRight className={`text-medical-300 flex-shrink-0 ${ctaVariant === 'booth' ? 'w-6 h-6' : 'w-5 h-5'}`} />
                    </div>
                  </div>
                </div>

                {/* Drafting view */}
                <div className="absolute inset-0 transition-all duration-500 flex flex-col" style={{ opacity: reportPhase === 'drafting' ? 1 : 0 }}>
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 pt-4 pb-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary-500" />
                      <p className="text-sm font-bold text-medical-900">Chronology Summary</p>
                    </div>
                    <span className="px-2.5 py-1 bg-primary-50 text-primary-700 text-[10px] font-medium rounded-full flex items-center gap-1">
                      <Zap className="w-3 h-3 text-primary-500 animate-pulse" />
                      Generating...
                    </span>
                  </div>
                  {/* Scrollable report body */}
                  <div ref={reportScrollRef} className="flex-1 px-5 overflow-y-auto pb-2">
                    <div className="space-y-3 text-[10.5px] text-medical-900 leading-relaxed">
                      {(() => {
                        let charsUsed = 0;
                        return reportSections.map((section, idx) => {
                          const sectionText = section.heading + section.body;
                          const sectionLen = sectionText.length;
                          const charsAvailable = Math.max(0, reportGenChars - charsUsed);
                          charsUsed += sectionLen;
                          if (charsAvailable <= 0) return null;
                          const headingLen = section.heading.length;
                          const headingVisible = Math.min(charsAvailable, headingLen);
                          const bodyChars = Math.max(0, charsAvailable - headingLen);
                          const bodyVisible = section.body.substring(0, bodyChars);
                          const isLastVisible = charsAvailable < sectionLen;
                          return (
                            <div key={idx}>
                              <p className="text-[10px] font-bold text-medical-700 uppercase tracking-wide mb-1">
                                {section.heading.substring(0, headingVisible)}
                              </p>
                              {bodyChars > 0 && (
                                <p>
                                  {bodyVisible}
                                  {isLastVisible && (
                                    <span className="inline-block w-[3px] h-[12px] bg-primary-500 ml-[1px] animate-pulse rounded-sm align-text-bottom" />
                                  )}
                                </p>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Screen 5: Paginated Bundle — features grid + sample page */}
            <div className="absolute inset-0 transition-opacity duration-500 flex flex-col" style={{ opacity: activeScreen === 5 ? 1 : 0, pointerEvents: activeScreen === 5 ? 'auto' : 'none' }}>
              <div className="flex-1 px-6 py-5 flex flex-col">
                {/* Header */}
                <div className={`flex items-center gap-2 ${ctaVariant === 'booth' ? 'mb-5' : 'mb-4'}`}>
                  <Layers className={`text-primary-600 ${ctaVariant === 'booth' ? 'w-7 h-7' : 'w-5 h-5'}`} />
                  <div>
                    <h3 className={`font-bold text-medical-900 leading-tight ${ctaVariant === 'booth' ? 'text-2xl' : 'text-base'}`}>Paginated Bundle</h3>
                    <p className={`text-medical-500 leading-tight ${ctaVariant === 'booth' ? 'text-base mt-0.5' : 'text-[11px]'}`}>Auto-generated, Bates-stamped, court-ready disclosure PDF</p>
                  </div>
                </div>

                {/* Body: value props */}
                <div className={`flex-1 flex flex-col justify-center max-w-2xl mx-auto w-full ${ctaVariant === 'booth' ? 'gap-4' : 'gap-3'}`}>
                  {[
                    { icon: Clock, label: 'Skip days of manual pagination', desc: 'No more paralegal hours stitching files together and Bates-stamping by hand', bg: 'bg-amber-50', icon_colour: 'text-amber-600', ring: 'ring-amber-300', wash: 'bg-gradient-to-br from-white via-white to-amber-50/70' },
                    { icon: AlertCircle, label: 'Court-ready, every time', desc: 'Consistent numbering and disclosure format judges and opposing counsel expect', bg: 'bg-rose-50', icon_colour: 'text-rose-600', ring: 'ring-rose-300', wash: 'bg-gradient-to-br from-white via-white to-rose-50/70' },
                    { icon: Zap, label: 'Ready when you need it', desc: 'Generate and share in a single click, the moment a bundle is required', bg: 'bg-emerald-50', icon_colour: 'text-emerald-600', ring: 'ring-emerald-300', wash: 'bg-gradient-to-br from-white via-white to-emerald-50/70' },
                  ].map((tile, i) => {
                    const isActive = bundleHighlight === i;
                    return (
                      <div
                        key={tile.label}
                        className={`rounded-xl border flex items-start transition-all duration-500 ${
                          ctaVariant === 'booth' ? `p-5 gap-5 ${tile.wash}` : 'p-4 gap-4 bg-white/90'
                        } ${
                          isActive
                            ? `border-medical-300 ring-2 ${tile.ring} scale-[1.02] shadow-md`
                            : 'border-medical-200/60'
                        }`}
                        style={{
                          opacity: activeScreen === 5 ? 1 : 0,
                          transform: activeScreen === 5
                            ? (isActive ? 'translateX(0) scale(1.02)' : 'translateX(0)')
                            : 'translateX(-12px)',
                          transitionDelay: activeScreen === 5 ? `${300 + i * 200}ms` : '0ms',
                        }}
                      >
                        <div className={`rounded-lg ${tile.bg} flex items-center justify-center flex-shrink-0 ${ctaVariant === 'booth' ? 'w-14 h-14' : 'w-10 h-10'}`}>
                          <tile.icon className={`${tile.icon_colour} ${ctaVariant === 'booth' ? 'w-7 h-7' : 'w-5 h-5'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className={`font-bold text-medical-900 leading-tight mb-1 ${ctaVariant === 'booth' ? 'text-xl' : 'text-sm'}`}>{tile.label}</p>
                          <p className={`text-medical-500 leading-snug ${ctaVariant === 'booth' ? 'text-base' : 'text-xs'}`} dangerouslySetInnerHTML={{ __html: tile.desc }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Screen 6: Standards / Causation / Evidence Integrity */}
            <div className="absolute inset-0 transition-opacity duration-500 flex flex-col" style={{ opacity: activeScreen === 6 ? 1 : 0, pointerEvents: activeScreen === 6 ? 'auto' : 'none' }}>
              <div className="flex-1 px-6 py-5 flex flex-col">
                {/* Header */}
                <div className={`flex items-center gap-2 ${ctaVariant === 'booth' ? 'mb-5' : 'mb-4'}`}>
                  <Eye className={`text-primary-600 ${ctaVariant === 'booth' ? 'w-7 h-7' : 'w-5 h-5'}`} />
                  <div>
                    <h3 className={`font-bold text-medical-900 leading-tight ${ctaVariant === 'booth' ? 'text-2xl' : 'text-base'}`}>Objective Analysis</h3>
                    <p className={`text-medical-500 leading-tight ${ctaVariant === 'booth' ? 'text-base mt-0.5' : 'text-[11px]'}`}>AI-assisted insight across standards, causation, and evidence integrity</p>
                  </div>
                </div>

                {/* Tiles */}
                <div className={`flex-1 flex flex-col justify-center ${ctaVariant === 'booth' ? 'gap-4 max-w-2xl mx-auto w-full' : 'gap-3'}`}>
                {[
                  { icon: AlertCircle, tab: 'Clinical Standards', desc: 'Identify breaches of duty without manually cross-referencing NICE, GMC, and specialty guidelines.', bg: 'bg-amber-50', icon_colour: 'text-amber-600', ring: 'ring-amber-300', wash: 'bg-gradient-to-br from-white via-white to-amber-50/70' },
                  { icon: LucideLink, tab: 'Causation', desc: 'Build a defensible causation argument that separates the index event from pre-existing conditions.', bg: 'bg-sky-50', icon_colour: 'text-sky-600', ring: 'ring-sky-300', wash: 'bg-gradient-to-br from-white via-white to-sky-50/70' },
                  { icon: Eye, tab: 'Evidence Integrity', desc: 'Catch missing records, contradictions, and gaps before opposing counsel does.', bg: 'bg-violet-50', icon_colour: 'text-violet-600', ring: 'ring-violet-300', wash: 'bg-gradient-to-br from-white via-white to-violet-50/70' },
                ].map((tile, i) => {
                  const isActive = analysisHighlight === i;
                  return (
                    <div
                      key={tile.tab}
                      className={`rounded-xl border flex items-start transition-all duration-500 ${
                        ctaVariant === 'booth' ? `p-5 gap-5 ${tile.wash}` : 'p-4 gap-4 bg-white/90'
                      } ${
                        isActive
                          ? `border-medical-300 ring-2 ${tile.ring} scale-[1.02] shadow-md`
                          : 'border-medical-200/60'
                      }`}
                      style={{
                        opacity: activeScreen === 6 ? 1 : 0,
                        transform: activeScreen === 6
                          ? (isActive ? 'translateX(0) scale(1.02)' : 'translateX(0)')
                          : 'translateX(-12px)',
                        transitionDelay: activeScreen === 6 ? `${250 + i * 200}ms` : '0ms',
                      }}
                    >
                      <div className={`rounded-lg ${tile.bg} flex items-center justify-center flex-shrink-0 ${ctaVariant === 'booth' ? 'w-14 h-14' : 'w-10 h-10'}`}>
                        <tile.icon className={`${tile.icon_colour} ${ctaVariant === 'booth' ? 'w-7 h-7' : 'w-5 h-5'}`} />
                      </div>
                      <div className="min-w-0">
                        <p className={`font-bold text-medical-900 leading-tight mb-1 ${ctaVariant === 'booth' ? 'text-xl' : 'text-sm'}`}>{tile.tab}</p>
                        <p className={`text-medical-600 leading-snug ${ctaVariant === 'booth' ? 'text-base' : 'text-xs'}`}>{tile.desc}</p>
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>

            {/* Screen 7: Enterprise / Trust — trust badges row */}
            <div className="absolute inset-0 transition-all duration-700 flex flex-col items-center justify-center" style={{ opacity: activeScreen === 7 ? 1 : 0, pointerEvents: activeScreen === 7 ? 'auto' : 'none' }}>
              <div className="text-center px-6 w-full mx-auto">
                <h2 className={`font-extrabold text-medical-900 ${ctaVariant === 'booth' ? 'text-[44px] leading-[1.05] mb-3 tracking-tight' : 'text-2xl mb-2'}`}>Enterprise-grade security.</h2>
                <p className={`text-medical-600 ${ctaVariant === 'booth' ? 'text-xl mb-10' : 'text-sm mb-10'}`}>Built from the ground up for sensitive medical data.</p>

                <div className={`grid grid-cols-4 mx-auto ${ctaVariant === 'booth' ? 'gap-5 max-w-4xl' : 'gap-3 max-w-3xl'}`}>
                  {[
                    { icon: Globe2, label: 'UK data', sublabel: 'residency' },
                    { icon: BadgeCheck, label: 'NHS-aligned', sublabel: 'standards' },
                    { icon: ShieldCheck, label: 'GDPR', sublabel: 'compliant' },
                    { icon: Lock, label: 'End-to-end', sublabel: 'encrypted' },
                  ].map((item, i) => (
                    <div
                      key={item.label}
                      className="flex flex-col items-center transition-all duration-500"
                      style={{
                        opacity: activeScreen === 7 ? 1 : 0,
                        transform: activeScreen === 7 ? 'translateY(0)' : 'translateY(12px)',
                        transitionDelay: `${300 + i * 200}ms`,
                      }}
                    >
                      <div className={`rounded-2xl border border-medical-200/80 shadow-sm flex items-center justify-center ${
                        ctaVariant === 'booth'
                          ? 'w-32 h-32 mb-4 bg-gradient-to-br from-white via-white to-primary-50/70'
                          : 'w-24 h-24 mb-3 bg-white'
                      }`}>
                        <item.icon className={`text-primary-600 ${ctaVariant === 'booth' ? 'w-14 h-14' : 'w-10 h-10'}`} strokeWidth={1.5} />
                      </div>
                      <p className={`font-semibold text-medical-900 leading-tight ${ctaVariant === 'booth' ? 'text-xl' : 'text-sm'}`}>{item.label}</p>
                      <p className={`text-medical-600 leading-tight ${ctaVariant === 'booth' ? 'text-lg' : 'text-sm'}`}>{item.sublabel}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Screen 8: CTA — varies by ctaVariant */}
            <div className="absolute inset-0 transition-opacity duration-700 flex flex-col items-center justify-center" style={{ opacity: activeScreen === 8 ? 1 : 0, pointerEvents: activeScreen === 8 ? 'auto' : 'none' }}>
              {ctaVariant === 'booth' ? (
                <div className="text-center px-6 w-full">
                  {/* Compact animated logo splash — branded bookend, sized to leave room for the headline */}
                  <div className="mb-3 flex items-center justify-center gap-2">
                    <svg viewBox="0 0 110 100" className="h-[40px] w-auto" style={{ overflow: 'visible' }}>
                      <line x1="22" y1="78" x2="55" y2="20" stroke="#1e3a5f" strokeWidth="9" strokeLinecap="round"
                        style={{
                          strokeDasharray: 70,
                          strokeDashoffset: boothSplashStep >= 4 ? 0 : 70,
                          transition: 'stroke-dashoffset 0.5s ease-out',
                        }}
                      />
                      <line x1="55" y1="20" x2="88" y2="78" stroke="#1e3a5f" strokeWidth="9" strokeLinecap="round"
                        style={{
                          strokeDasharray: 70,
                          strokeDashoffset: boothSplashStep >= 4 ? 0 : 70,
                          transition: 'stroke-dashoffset 0.5s ease-out 0.1s',
                        }}
                      />
                      <line x1="22" y1="78" x2="88" y2="78" stroke="#1e3a5f" strokeWidth="9" strokeLinecap="round"
                        style={{
                          strokeDasharray: 70,
                          strokeDashoffset: boothSplashStep >= 4 ? 0 : 70,
                          transition: 'stroke-dashoffset 0.5s ease-out 0.2s',
                        }}
                      />
                      <circle cx="22" cy="78" r="14" fill="#5dc8d6"
                        style={{
                          opacity: boothSplashStep >= 1 ? 1 : 0,
                          transform: boothSplashStep >= 1 ? 'scale(1)' : 'scale(0)',
                          transformOrigin: '22px 78px',
                          transition: 'opacity 0.3s ease-out, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        }}
                      />
                      <circle cx="55" cy="20" r="14" fill="#1e3a5f"
                        style={{
                          opacity: boothSplashStep >= 2 ? 1 : 0,
                          transform: boothSplashStep >= 2 ? 'scale(1)' : 'scale(0)',
                          transformOrigin: '55px 20px',
                          transition: 'opacity 0.3s ease-out, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        }}
                      />
                      <circle cx="88" cy="78" r="14" fill="#1e3a5f"
                        style={{
                          opacity: boothSplashStep >= 3 ? 1 : 0,
                          transform: boothSplashStep >= 3 ? 'scale(1)' : 'scale(0)',
                          transformOrigin: '88px 78px',
                          transition: 'opacity 0.3s ease-out, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        }}
                      />
                    </svg>
                    <div className="flex flex-col items-start"
                      style={{
                        opacity: boothSplashStep >= 5 ? 1 : 0,
                        transform: boothSplashStep >= 5 ? 'translateX(0)' : 'translateX(-12px)',
                        transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                      }}
                    >
                      <span className="font-bold text-[34px] leading-none tracking-tight text-medical-900" style={{ fontFamily: "'Nunito Sans', sans-serif" }}>
                        ClinLexis
                        <sup className="text-[10px] font-normal opacity-50 text-medical-400 ml-[-2px] align-super">™</sup>
                      </span>
                    </div>
                  </div>

                  {/* Big headline — primary booth value prop. "Welcome to our stand" reads as the soft greeting below. */}
                  <h2
                    className="text-[52px] font-extrabold text-medical-900 leading-[0.95] tracking-tight mb-3"
                    style={{
                      opacity: boothSplashStep >= 6 ? 1 : 0,
                      transform: boothSplashStep >= 6 ? 'translateY(0)' : 'translateY(10px)',
                      transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                    }}
                  >
                    Reduce case prep<br />
                    <span className="text-primary-600">from days to hours.</span>
                  </h2>
                  <p
                    className="text-xl font-medium text-medical-700 mb-5"
                    style={{
                      opacity: boothSplashStep >= 6 ? 1 : 0,
                      transform: boothSplashStep >= 6 ? 'translateY(0)' : 'translateY(10px)',
                      transition: 'opacity 0.5s ease-out 0.15s, transform 0.5s ease-out 0.15s',
                    }}
                  >
                    Welcome to our stand.
                  </p>

                  {/* Conference-exclusive trial — boxed, amber, prominent secondary CTA */}
                  <div
                    className="mx-auto max-w-xl bg-amber-50 border-2 border-amber-300 rounded-2xl shadow-md px-5 py-4 flex items-center gap-5"
                    style={{
                      opacity: boothSplashStep >= 7 ? 1 : 0,
                      transform: boothSplashStep >= 7 ? 'translateY(0)' : 'translateY(10px)',
                      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
                    }}
                  >
                    <div className="flex flex-col items-center flex-shrink-0">
                      <img
                        src="https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=https%3A%2F%2Fclinlexis.co.uk%2Fconference&color=1e3a5f&bgcolor=ffffff&qzone=1"
                        alt="QR code to clinlexis.co.uk/conference"
                        className="w-[120px] h-[120px] rounded-md border border-amber-200 bg-white"
                      />
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-amber-700 mb-1.5">Conference exclusive</p>
                      <p className="text-[24px] font-extrabold text-medical-900 leading-[1.1] tracking-tight mb-1.5">
                        Scan for a conference-only trial<br />or book a personalised demo
                      </p>
                      <p className="text-sm font-semibold text-medical-700">clinlexis.co.uk/conference</p>
                    </div>
                  </div>
                </div>
              ) : ctaVariant === 'signup' ? (
                <div className="text-center px-8">
                  <div className="mb-6 flex justify-center">
                    <Logo size="lg" />
                  </div>
                  <p className="text-2xl font-extrabold text-medical-900 leading-tight mb-3">
                    Try it on your next case.
                  </p>
                  <div className="flex items-baseline justify-center gap-1.5 mb-1">
                    <span className="text-3xl font-extrabold text-medical-900">£24.99</span>
                    <span className="text-sm text-medical-500">/month</span>
                  </div>
                  <p className="text-sm text-medical-600 max-w-sm mx-auto mb-1">
                    <span className="font-semibold text-primary-700">1,000 complimentary pages</span> to get going
                  </p>
                  <p className="text-sm text-medical-500 mb-7">
                    Consumption-based pricing as you scale &middot; cancel any time
                  </p>
                  <div className="inline-flex items-center justify-center px-8 py-3.5 bg-primary-600 text-white rounded-xl font-semibold text-base shadow-lg shadow-primary-600/20">
                    Start your first case
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </div>
                  <p className="text-sm text-medical-500 mt-4">clinlexis.co.uk/signup</p>
                </div>
              ) : (
                <div className="text-center px-8">
                  <div className="mb-7 flex justify-center">
                    <Logo size="lg" />
                  </div>
                  <p className="text-base text-medical-600 mb-8 max-w-md mx-auto">
                    Book a free 30-minute demo to see how ClinLexis™ fits your case preparation.
                  </p>
                  <div className="inline-flex items-center justify-center px-8 py-3.5 bg-primary-600 text-white rounded-xl font-semibold text-base shadow-lg shadow-primary-600/20">
                    Book Your Demo
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </div>
                  <p className="text-sm text-medical-500 mt-4">clinlexis.co.uk/demo</p>
                </div>
              )}
            </div>

          </div>

          {/* Bottom caption — booth: static formatted (bold + line breaks); others: audio-driven karaoke highlight.
              Booth hides the strip on screens 0 (intro splash) and 8 (CTA) since both already carry the message in-frame. */}
          {!(ctaVariant === 'booth' && (activeScreen === 0 || activeScreen === 8)) && (
            <div className={`px-5 border-t border-primary-200 bg-primary-50/70 ${ctaVariant === 'booth' ? 'py-4' : 'py-3'}`}>
              {ctaVariant === 'booth' ? (
                <p className="text-2xl text-center min-h-[72px] flex items-center justify-center leading-snug font-medium text-medical-800">
                  <span>{boothCaptions[activeScreen]}</span>
                </p>
              ) : (
                <p className="text-base text-center h-[48px] flex items-center justify-center flex-wrap gap-x-1">
                  {screenCaptions[activeScreen].split(/\s+/).map((word, i) => (
                    <span
                      key={`${activeScreen}-${i}`}
                      className={`font-semibold transition-colors duration-150 ${
                        i <= spokenWordIndex
                          ? 'text-primary-800'
                          : 'text-medical-500'
                      }`}
                    >
                      {word}
                    </span>
                  ))}
                </p>
              )}
            </div>
          )}
      </div>
      )}
    </div>
  );
};
